/* Cellar showroom — a to-scale PBR view of the room the calculator quoted.
 *
 * Self-initialising ES module. Reads the same form (#WccQuoteForm) and catalog
 * (#wcc-quote-data) the 2D quote reads, recomputes with the same engine
 * (window.WCCQuote), and draws the result. Parts are MODELED (not photo-mapped)
 * with strong procedural normal/bump maps so metal, wood, plaster, glass and
 * paper clearly read as real materials. Fully gated: any failure hides the
 * stage and leaves the 2D quote untouched.
 *
 * Features:
 *  - Multi-unit: renders every unit the quote calls for (pick qty), spaced to
 *    scale on the chosen wall.
 *  - Real racking: VintageView/Ultra-style wall rails, bottles perpendicular to
 *    the wall (label/capsule forward) at realistic pitch, filling the real
 *    quoted bottle count and WRAPPING onto additional walls when one fills.
 *  - Door + glass: an entry on the front wall reflecting the glass-wall / door
 *    inputs, with a "From the door" view.
 *  - Share: inputs are encoded in the URL; a printable spec sheet and an
 *    "email this cellar" link are generated on the fly.
 *
 * Performance model:
 *  - ONE WebGLRenderer + ONE environment map + ONE material set, reused across
 *    re-renders; wall toggles rebuild only the scene graph. Render-on-demand
 *    loop pauses off-screen. Cheap proxy-box picking. Low-power device tier.
 *
 * Default view is a fast, print-friendly to-scale BLUEPRINT (2D canvas, no
 * WebGL). three.js is only fetched/instantiated when the shopper taps "3D view"
 * (dynamic import via the import map) — so the 670KB library and a GL context
 * never load unless requested. One foot = one world unit.
 */
let THREE = null;
async function loadThree() { if (!THREE) { THREE = await import('three'); } return THREE; }

const ready = (fn) => document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn();
const DEBUG = (() => { try { return !!localStorage.getItem('wccDebug'); } catch (e) { return false; } })();
const warn = (...a) => { if (DEBUG) try { console.warn('[wcc-3d]', ...a); } catch (e) {} };

/* ---- Klaviyo lead capture (uses the storefront's live onsite object) ---- */
const KL_COMPANY = 'VhHzAe';       // WCC public company id (Klaviyo onsite)
const KL_LIST = 'TnFnNN';          // "Broadcast" marketing list (double opt-in → Klaviyo emails a confirmation)
function klaviyoObj() { try { return (typeof window !== 'undefined' && window.klaviyo) || null; } catch (e) { return null; } }
function validEmail(e) { return /.+@.+\..+/.test(e || ''); }
/* The page restamps its BTU figure with the full heat-load total from
 * wcc-load-detail; the tooltip, spec sheet and lead props must match it.
 * Falls back to the old estimate when the detail snippet is absent. */
function loadBtu(q) {
  try {
    if (typeof window.WCCLoadTotal === 'function') {
      const t = window.WCCLoadTotal();
      if (t != null && isFinite(t)) return Math.round(t);
    }
  } catch (e) {}
  return (q && q.btu) || 0;
}
function leadProps(input, quote, extra) {
  input = input || {}; quote = quote || {};
  const p = quote.picks && quote.picks[0], r = quote.racks && quote.racks[0];
  const props = {
    cellar_l_ft: input.length, cellar_w_ft: input.width, cellar_h_ft: input.height,
    cellar_volume_cuft: Math.round((input.length || 0) * (input.width || 0) * (input.height || 0)),
    bottles: input.bottles || 0, ambient_f: input.ambient, glass_wall: !!input.glass, standard_door: !!input.standardDoor,
    unit: p ? p.unit.title : null, unit_qty: p ? p.qty : null,
    rack: r ? r.rack.title : null, rack_qty: r ? r.qty : null,
    estimated_btu: loadBtu(quote) || null, quote_total: (p ? p.total : 0) + (r ? r.total : 0)
  };
  if (extra) for (const k in extra) props[k] = extra[k];
  return props;
}
function captureLead(email, input, quote, extra, optIn) {
  const k = klaviyoObj(); if (!k || !validEmail(email)) return false;
  let quoteUrl = location.href; try { if (state.lastForm) quoteUrl = shareUrl(state.lastForm, state.unitWall); } catch (e) {}
  const props = leadProps(input, quote, Object.assign({ lead_source: 'Cellar Showroom', marketing_opt_in: !!optIn, quote_url: quoteUrl }, extra || {}));
  try { k.identify({ email: email, properties: props }); } catch (e) { warn('kl identify', e); }
  try { k.track('Built Cellar Quote', props); } catch (e) { warn('kl track', e); }
  if (optIn) subscribeLead(email);
  return true;
}
function subscribeLead(email) {
  const k = klaviyoObj();
  try { if (k) k.identify({ email: email, properties: { accepts_marketing: true, $consent: ['email'] } }); } catch (e) {}
  if (!KL_LIST) return; // no list configured yet → consent flagged on the profile; the store subscribes opted-in profiles via a segment/flow
  try {
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KL_COMPANY, {
      method: 'POST', headers: { 'content-type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({ data: { type: 'subscription', attributes: { custom_source: 'Cellar Showroom', profile: { data: { type: 'profile', attributes: { email: email } } } }, relationships: { list: { data: { type: 'list', id: KL_LIST } } } } })
    }).catch(() => {});
  } catch (e) { warn('kl subscribe', e); }
}

const mm = (q) => { try { return window.matchMedia(q).matches; } catch (e) { return false; } };
const REDUCED_MOTION = mm('(prefers-reduced-motion: reduce)');
const LOW_POWER = mm('(pointer: coarse)') || (navigator.hardwareConcurrency || 8) <= 4;

function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
function thumb(src, w) { if (!src) return ''; return /(\?|&)width=/.test(src) ? src : src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=' + (w || 340); }
const CALC_PATH = '/pages/cellar-cooling-calculator';

const pick = (form, k) => form.querySelector(`[data-q="${k}"]`);
const fval = (form, k) => { const el = pick(form, k); if (!el) return null; return el.type === 'checkbox' ? el.checked : el.value; };
const fnum = (form, k) => parseFloat(fval(form, k));
function readInput(form) {
  return {
    length: fnum(form, 'length'), width: fnum(form, 'width'), height: fnum(form, 'height'),
    ambient: fnum(form, 'ambient') || 75, rFactor: fnum(form, 'rfactor') || 0.055,
    vapourBarrier: pick(form, 'vapor') ? fval(form, 'vapor') === true : true,
    glass: fval(form, 'glass') === true, standardDoor: fval(form, 'stddoor') === true,
    bottles: parseInt(fval(form, 'bottles'), 10) || 0, preparedFor: '', jobRef: ''
  };
}
function catalog() { const el = document.getElementById('wcc-quote-data'); if (!el) return null; try { return JSON.parse(el.textContent); } catch (e) { warn('catalog parse failed', e); return null; } }

/* ---- URL share state --------------------------------------------------- */
const URLKEYS = { length: 'l', width: 'w', height: 'h', bottles: 'b', ambient: 'a', rfactor: 'r', vapor: 'v', glass: 'g', stddoor: 'd' };
function readUrlParams() {
  try {
    const p = new URLSearchParams(location.search); const o = {};
    for (const k in URLKEYS) { const v = p.get(URLKEYS[k]); if (v != null) o[k] = v; }
    return Object.keys(o).length ? o : null;
  } catch (e) { return null; }
}
function applyUrlToForm(form) {
  const o = readUrlParams(); if (!o || !form) return false;
  for (const k in o) { const el = pick(form, k); if (!el) continue; if (el.type === 'checkbox') el.checked = (o[k] === '1' || o[k] === 'true'); else el.value = o[k]; }
  return true;
}
function shareUrl(form, wall) {
  const q = new URLSearchParams();
  const put = (k, v) => { if (v != null && v !== '' && !(typeof v === 'number' && isNaN(v))) q.set(URLKEYS[k], v); };
  put('length', fval(form, 'length')); put('width', fval(form, 'width')); put('height', fval(form, 'height')); put('bottles', fval(form, 'bottles'));
  const amb = pick(form, 'ambient'); if (amb) put('ambient', amb.value);
  if (pick(form, 'vapor')) q.set(URLKEYS.vapor, fval(form, 'vapor') ? '1' : '0');
  if (pick(form, 'glass')) q.set(URLKEYS.glass, fval(form, 'glass') ? '1' : '0');
  if (pick(form, 'stddoor')) q.set(URLKEYS.stddoor, fval(form, 'stddoor') ? '1' : '0');
  if (wall) q.set('uw', wall);
  return location.origin + CALC_PATH + '?' + q.toString();
}

/* ---- procedural height→normal maps ------------------------------------- */
function heightNormal(size, draw, strength, repeat) {
  const hc = document.createElement('canvas'); hc.width = hc.height = size; const h = hc.getContext('2d');
  h.fillStyle = '#808080'; h.fillRect(0, 0, size, size); draw(h, size);
  const src = h.getImageData(0, 0, size, size).data;
  const oc = document.createElement('canvas'); oc.width = oc.height = size; const o = oc.getContext('2d');
  const img = o.createImageData(size, size); const d = img.data;
  const g = (x, y) => { x = (x + size) % size; y = (y + size) % size; return src[(y * size + x) * 4] / 255; };
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const dx = (g(x - 1, y) - g(x + 1, y)) * strength, dy = (g(x, y - 1) - g(x, y + 1)) * strength;
    const len = Math.hypot(dx, dy, 1), i = (y * size + x) * 4;
    d[i] = (dx / len * .5 + .5) * 255; d[i + 1] = (dy / len * .5 + .5) * 255; d[i + 2] = (1 / len * .5 + .5) * 255; d[i + 3] = 255;
  }
  o.putImageData(img, 0, 0); const t = new THREE.CanvasTexture(oc); t.wrapS = t.wrapT = THREE.RepeatWrapping; if (repeat) t.repeat.set(repeat, repeat); return t;
}
function brushedMetal(colorHex) {
  const c = document.createElement('canvas'); c.width = c.height = 512; const x = c.getContext('2d');
  x.fillStyle = colorHex; x.fillRect(0, 0, 512, 512);
  for (let i = 0; i < 14000; i++) { const v = 110 + Math.random() * 110; x.strokeStyle = `rgba(${v},${v},${v},0.06)`; const y = Math.random() * 512; x.beginPath(); x.moveTo(0, y); x.lineTo(512, y + (Math.random() * 2 - 1)); x.stroke(); }
  const map = new THREE.CanvasTexture(c); map.wrapS = map.wrapT = THREE.RepeatWrapping; map.colorSpace = THREE.SRGBColorSpace;
  const normal = heightNormal(512, (h) => { for (let i = 0; i < 7000; i++) { const v = Math.random() * 255; h.strokeStyle = `rgb(${v},${v},${v})`; const y = Math.random() * 512; h.beginPath(); h.moveTo(0, y); h.lineTo(512, y); h.stroke(); } }, 2.0);
  return { map, normal };
}
function woodMaps() {
  const c = document.createElement('canvas'); c.width = c.height = 1024; const x = c.getContext('2d');
  const grd = x.createLinearGradient(0, 0, 1024, 1024); grd.addColorStop(0, '#5b4128'); grd.addColorStop(.5, '#6d4f30'); grd.addColorStop(1, '#4f3924');
  x.fillStyle = grd; x.fillRect(0, 0, 1024, 1024); const plank = 128;
  for (let py = 0; py < 1024; py += plank) {
    const shift = (py / plank) % 2 ? 256 : 0;
    for (let i = 0; i < 6000; i++) { x.strokeStyle = `rgba(${28 + Math.random() * 46},${18 + Math.random() * 28},${8 + Math.random() * 16},${Math.random() * 0.09})`; x.beginPath(); const yy = py + Math.random() * plank; x.moveTo(Math.random() * 1024, yy); x.lineTo(Math.random() * 1024, yy + (Math.random() * 5 - 2.5)); x.stroke(); }
    x.strokeStyle = 'rgba(0,0,0,0.6)'; x.lineWidth = 3; x.beginPath(); x.moveTo(0, py); x.lineTo(1024, py); x.stroke();
    for (let sx = shift; sx <= 1024 + shift; sx += 256) { x.beginPath(); x.moveTo(sx % 1024, py); x.lineTo(sx % 1024, py + plank); x.stroke(); }
  }
  const map = new THREE.CanvasTexture(c); map.wrapS = map.wrapT = THREE.RepeatWrapping; map.colorSpace = THREE.SRGBColorSpace; map.anisotropy = 16;
  const normal = heightNormal(1024, (h) => {
    const p = 128; h.strokeStyle = '#000'; h.lineWidth = 5;
    for (let py = 0; py <= 1024; py += p) { h.beginPath(); h.moveTo(0, py); h.lineTo(1024, py); h.stroke(); const shift = (py / p) % 2 ? 256 : 0; for (let sx = shift; sx <= 1024 + shift; sx += 256) { h.beginPath(); h.moveTo(sx % 1024, py); h.lineTo(sx % 1024, py + p); h.stroke(); } }
    for (let i = 0; i < 2600; i++) { const v = 80 + Math.random() * 100; h.strokeStyle = `rgb(${v},${v},${v})`; const y = Math.random() * 1024; h.beginPath(); h.moveTo(0, y); h.lineTo(1024, y); h.stroke(); }
  }, 3.2); normal.anisotropy = 16;
  return { map, normal };
}
function plasterMaps(hex) {
  const c = document.createElement('canvas'); c.width = c.height = 512; const x = c.getContext('2d'); x.fillStyle = hex; x.fillRect(0, 0, 512, 512);
  for (let i = 0; i < 34000; i++) { x.fillStyle = `rgba(0,0,0,${Math.random() * 0.045})`; x.fillRect(Math.random() * 512, Math.random() * 512, 1, 1); }
  const map = new THREE.CanvasTexture(c); map.wrapS = map.wrapT = THREE.RepeatWrapping; map.colorSpace = THREE.SRGBColorSpace;
  const normal = heightNormal(512, (h) => { for (let i = 0; i < 14000; i++) { const v = Math.random() * 255; h.fillStyle = `rgb(${v},${v},${v})`; h.fillRect(Math.random() * 512, Math.random() * 512, 2, 2); } }, 0.9, 3);
  return { map, normal };
}
function paperNormal() { return heightNormal(256, (h) => { for (let i = 0; i < 16000; i++) { const v = 80 + Math.random() * 130; h.fillStyle = `rgb(${v},${v},${v})`; h.fillRect(Math.random() * 256, Math.random() * 256, 1, 1); } }, 0.6); }
function contactShadowTexture() {
  const c = document.createElement('canvas'); c.width = c.height = 256; const x = c.getContext('2d');
  const g = x.createRadialGradient(128, 128, 10, 128, 128, 128); g.addColorStop(0, 'rgba(0,0,0,0.55)'); g.addColorStop(1, 'rgba(0,0,0,0)');
  x.fillStyle = g; x.fillRect(0, 0, 256, 256); const t = new THREE.CanvasTexture(c); return t;
}

/* ---- geometry ---------------------------------------------------------- */
function bottleGeometry(diam, length, seg) {
  const p = [[0.00, 0.00], [0.30, 0.00], [0.46, 0.01], [0.50, 0.03], [0.50, 0.52], [0.49, 0.575], [0.40, 0.63], [0.24, 0.70], [0.135, 0.775], [0.125, 0.90], [0.125, 0.95], [0.145, 0.955], [0.145, 0.985], [0.11, 1.00]].map(([r, h]) => new THREE.Vector2(r, h));
  const geo = new THREE.LatheGeometry(p, seg || 36); geo.scale(diam, length, diam); geo.rotateX(Math.PI / 2); geo.computeVertexNormals(); return geo;
}
function foilGeometry(diam, length) { const g = new THREE.CylinderGeometry(diam * 0.17, diam * 0.15, length * 0.14, 20); g.rotateX(Math.PI / 2); return g; }

/* ---- shared material set (built once, reused) -------------------------- */
function buildMats() {
  return { wood: woodMaps(), plaster: plasterMaps('#efe9df'), steel: brushedMetal('#c6cbd0'), paper: paperNormal(), contact: contactShadowTexture() };
}

/* ---- room + door ------------------------------------------------------- */
/* Returns { walls } — the four vertical walls tagged with outward normals so
 * the render loop can hide whichever sit between the camera and the interior
 * (dollhouse cutaway). The door + glass live on the front wall and are NOT in
 * the cutaway set, so the entry always reads. */
function buildRoom(group, L, W, H, mats, opts) {
  opts = opts || {};
  const floorMat = new THREE.MeshStandardMaterial({ map: mats.wood.map, normalMap: mats.wood.normal, normalScale: new THREE.Vector2(1.0, 1.0), roughness: 0.5, metalness: 0, envMapIntensity: 0.6 });
  mats.wood.map.repeat.set(Math.max(1, L / 3), Math.max(1, W / 3)); mats.wood.normal.repeat.set(Math.max(1, L / 3), Math.max(1, W / 3));
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(L, W), floorMat);
  floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true; group.add(floor);
  const wallMat = new THREE.MeshStandardMaterial({ map: mats.plaster.map, normalMap: mats.plaster.normal, normalScale: new THREE.Vector2(0.6, 0.6), roughness: 0.96, metalness: 0, envMapIntensity: 0.3, transparent: true, opacity: 1 });
  wallMat.map.repeat.set(L / 4, H / 4); wallMat.normalMap.repeat.set(L / 4, H / 4);
  const walls = [];
  const back = new THREE.Mesh(new THREE.PlaneGeometry(L, H), wallMat); back.position.set(0, H / 2, -W / 2); back.receiveShadow = true; back.userData.normal = new THREE.Vector3(0, 0, -1); group.add(back); walls.push(back);
  const left = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat.clone()); left.rotation.y = Math.PI / 2; left.position.set(-L / 2, H / 2, 0); left.receiveShadow = true; left.userData.normal = new THREE.Vector3(-1, 0, 0); group.add(left); walls.push(left);
  const right = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat.clone()); right.rotation.y = -Math.PI / 2; right.position.set(L / 2, H / 2, 0); right.receiveShadow = true; right.userData.normal = new THREE.Vector3(1, 0, 0); group.add(right); walls.push(right);
  const front = new THREE.Mesh(new THREE.PlaneGeometry(L, H), wallMat.clone()); front.rotation.y = Math.PI; front.position.set(0, H / 2, W / 2); front.receiveShadow = true; front.userData.normal = new THREE.Vector3(0, 0, 1); group.add(front); walls.push(front);
  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(L, W), new THREE.MeshStandardMaterial({ color: 0xe9e3d8, roughness: 1, metalness: 0, side: THREE.DoubleSide })); ceil.rotation.x = Math.PI / 2; ceil.position.set(0, H, 0); ceil.userData.isCeiling = true; group.add(ceil);
  const trim = new THREE.MeshStandardMaterial({ color: 0x1c1712, roughness: 0.35, metalness: 0.15 });
  const bb1 = new THREE.Mesh(new THREE.BoxGeometry(L, 0.42, 0.1), trim); bb1.position.set(0, 0.21, -W / 2 + 0.05); bb1.castShadow = true; group.add(bb1);
  const bb2 = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.42, W), trim); bb2.position.set(-L / 2 + 0.05, 0.21, 0); bb2.castShadow = true; group.add(bb2);
  buildDoor(group, L, W, H, opts.glass, opts.standardDoor);
  return walls;
}
/* Entry on the front wall (z = +W/2). Glass wall/door → a translucent pane;
 * standard door → a solid panel; otherwise just a framed opening. */
function buildDoor(group, L, W, H, glass, standardDoor) {
  const dw = Math.min(3.0, L * 0.6), dh = Math.min(6.8, H * 0.94);
  const z = W / 2 - 0.03, y0 = 0;
  const frameMat = new THREE.MeshStandardMaterial({ color: 0x2a2019, roughness: 0.5, metalness: 0.2 });
  const jamb = 0.12;
  const mk = (w, h, x, y) => { const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.14), frameMat); m.position.set(x, y, z); m.castShadow = true; group.add(m); };
  mk(dw + jamb * 2, jamb, 0, dh);                         // header
  mk(jamb, dh, -dw / 2 - jamb / 2, dh / 2);               // left jamb
  mk(jamb, dh, dw / 2 + jamb / 2, dh / 2);                // right jamb
  if (glass) {
    // tinted, reflective glass so it clearly reads as glass (not an empty opening)
    const gmat = LOW_POWER
      ? new THREE.MeshStandardMaterial({ color: 0xcfe8ee, roughness: 0.08, metalness: 0, transparent: true, opacity: 0.36, envMapIntensity: 1.3, side: THREE.DoubleSide })
      : new THREE.MeshPhysicalMaterial({ color: 0xd3ebf0, roughness: 0.06, metalness: 0, transmission: 0.4, transparent: true, opacity: 0.55, reflectivity: 0.8, clearcoat: 1, clearcoatRoughness: 0.05, ior: 1.5, envMapIntensity: 1.6, side: THREE.DoubleSide });
    const pane = new THREE.Mesh(new THREE.PlaneGeometry(dw, dh), gmat);
    pane.position.set(0, dh / 2, z - 0.01); group.add(pane);
    // slim brushed-metal frame rail + handle read as a modern glass door
    const trimMat = new THREE.MeshStandardMaterial({ color: 0x9aa0a6, roughness: 0.34, metalness: 0.88, envMapIntensity: 1.2 });
    const rail = new THREE.Mesh(new THREE.BoxGeometry(dw, 0.05, 0.06), trimMat); rail.position.set(0, dh * 0.42, z + 0.02); group.add(rail);
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 1.1, 12), new THREE.MeshStandardMaterial({ color: 0xd0d4d7, roughness: 0.25, metalness: 0.95, envMapIntensity: 1.3 }));
    handle.position.set(dw * 0.34, dh * 0.5, z - 0.06); group.add(handle);
  } else if (standardDoor) {
    // solid door shown SWUNG OPEN (hinged at the left jamb) so it reads as a real
    // door without blocking the cellar — matches the blueprint's swing arc
    const g = new THREE.Group(); g.position.set(-dw / 2, dh / 2, z); g.rotation.y = -1.05; // ~60° open: reads as a door, doesn't wall off the cellar
    const woodMat = new THREE.MeshStandardMaterial({ color: 0x6d4f30, roughness: 0.62, metalness: 0.05, envMapIntensity: 0.5 });
    const panel = new THREE.Mesh(new THREE.BoxGeometry(dw, dh, 0.06), woodMat); panel.position.set(dw / 2, 0, 0); panel.castShadow = true; g.add(panel);
    const inset = new THREE.Mesh(new THREE.BoxGeometry(dw * 0.66, dh * 0.82, 0.02), new THREE.MeshStandardMaterial({ color: 0x5b4127, roughness: 0.68, metalness: 0.05 })); inset.position.set(dw / 2, 0, 0.035); g.add(inset);
    const knob = new THREE.Mesh(new THREE.SphereGeometry(0.055, 14, 14), new THREE.MeshStandardMaterial({ color: 0xcfa14a, roughness: 0.3, metalness: 0.9, envMapIntensity: 1.2 })); knob.position.set(dw * 0.9, 0, 0.09); g.add(knob);
    group.add(g);
  }
}

/* One cooling-unit grille on `wall`, sized to scale from unit.dim (inches),
 * positioned `uOffset` feet along the wall from centre (for multi-unit spacing).
 * Adds a pick proxy. Returns { topBandY, onBack }. */
function buildUnit(group, L, W, H, mats, unit, wall, uOffset, label, pickables) {
  const dim = unit && unit.dim;
  let fw = dim && dim.w ? dim.w / 12 : 1.5, fh = dim && dim.h ? dim.h / 12 : 1.25;
  fw = Math.min(fw, (wall === 'back' ? L : W) * 0.4); fh = Math.min(fh, H * 0.34);
  const g = new THREE.Group(); g.userData.wccLabel = label;
  const steel = new THREE.MeshStandardMaterial({ map: mats.steel.map, normalMap: mats.steel.normal, normalScale: new THREE.Vector2(0.6, 0.6), color: 0xdadee1, roughness: 0.28, metalness: 0.92, envMapIntensity: 1.4 });
  const frame = new THREE.Mesh(new THREE.BoxGeometry(fw * 1.08, fh * 1.08, 0.05), steel); frame.position.z = 0.02; frame.castShadow = true; g.add(frame);
  const bay = new THREE.Mesh(new THREE.BoxGeometry(fw, fh, 0.16), new THREE.MeshStandardMaterial({ color: 0x15181b, roughness: 0.8, metalness: 0.2 })); bay.position.z = -0.09; g.add(bay);
  const louverMat = new THREE.MeshStandardMaterial({ map: mats.steel.map, normalMap: mats.steel.normal, color: 0xaab0b5, roughness: 0.3, metalness: 0.85, envMapIntensity: 1.3 });
  const n = 18, gh = fh * 0.74, y0 = -gh / 2, step = gh / (n - 1);
  for (let i = 0; i < n; i++) { const lv = new THREE.Mesh(new THREE.BoxGeometry(fw * 0.82, step * 0.5, 0.09), louverMat); lv.position.set(0, y0 + i * step, -0.01); lv.rotation.x = -0.5; g.add(lv); }
  const readout = new THREE.Mesh(new THREE.PlaneGeometry(fw * 0.18, fh * 0.08), new THREE.MeshStandardMaterial({ color: 0x0a3a24, emissive: 0x1ad07f, emissiveIntensity: 1.1, roughness: 0.4 })); readout.position.set(fw * 0.28, fh * 0.4, 0.03); g.add(readout);
  const proxy = new THREE.Mesh(new THREE.BoxGeometry(fw * 1.25, fh * 1.35, 0.9), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })); proxy.userData.wccLabel = label; proxy.position.z = 0.2; g.add(proxy); if (pickables) pickables.push(proxy);
  const cy = H - fh / 2 - (wall === 'back' ? 0.35 : 0.55);
  const e = 0.001;
  if (wall === 'left') { g.rotation.y = Math.PI / 2; g.position.set(-L / 2 + e, cy, uOffset); }
  else if (wall === 'right') { g.rotation.y = -Math.PI / 2; g.position.set(L / 2 - e, cy, -uOffset); }
  else { g.rotation.y = 0; g.position.set(uOffset, cy, -W / 2 + e); }
  group.add(g);
  return { topBandY: cy - fh / 2 - 0.25, onBack: wall === 'back', fw: fw };
}

/* wall basis for racking: interior-facing plane, horizontal axis, out-normal */
function wallBasis(wall, L, W) {
  if (wall === 'back') return { plane: new THREE.Vector3(0, 0, -W / 2), u: new THREE.Vector3(1, 0, 0), out: new THREE.Vector3(0, 0, 1), width: L };
  if (wall === 'left') return { plane: new THREE.Vector3(-L / 2, 0, 0), u: new THREE.Vector3(0, 0, 1), out: new THREE.Vector3(1, 0, 0), width: W };
  return { plane: new THREE.Vector3(L / 2, 0, 0), u: new THREE.Vector3(0, 0, -1), out: new THREE.Vector3(-1, 0, 0), width: W };
}

/* VintageView / Ultra metal wall-rail racking, faithful to the real product:
 * thin vertical rails, bottles held PERPENDICULAR to the wall (capsule forward),
 * tilted slightly down, at realistic ~4in pitch. Fills the real quoted bottle
 * count, WRAPPING onto extra walls (never the unit's wall or the door wall) when
 * one fills. Returns { shown, holds, truncated, wallsUsed }. */
function buildRacks(group, L, W, H, holds, mats, rackWalls, label, pickables, maxDraw, dim) {
  if (!holds || holds < 1 || !rackWalls.length) return null;
  const orient = (dim && dim.orient) || 'label';
  const isLabel = orient === 'label';
  const inFt = (v, d) => (v ? v / 12 : d);
  const hCell = isLabel ? inFt(dim && dim.colW, 1.083) : inFt(dim && dim.colW, 0.375); // horizontal footprint per bottle
  const vPitch = inFt(dim && dim.pitch, 0.333);
  const bottleD = 0.30, bottleLen = isLabel ? Math.min(hCell * 0.92, 1.0) : 1.0;
  const projFt = isLabel ? inFt(dim && dim.depth, 0.42) : 1.0; // projection into the room
  const baseY = 0.55, topPad = 0.45;
  const usableH = Math.max(1, H - baseY - topPad);
  const rows = Math.max(1, Math.floor(usableH / vPitch));
  const seg = LOW_POWER ? 16 : 30;
  const cap = maxDraw || 900;

  const plan = [];
  let remaining = Math.min(holds, cap);
  for (let w = 0; w < rackWalls.length; w++) {
    const b = wallBasis(rackWalls[w], L, W);
    const cols = Math.max(1, Math.floor((b.width * 0.9) / hCell));
    const take = Math.max(0, Math.min(remaining, cols * rows));
    plan.push({ wall: rackWalls[w], basis: b, cols: cols, take: take });
    remaining -= take;
    if (remaining <= 0) break;
  }
  const drawn = plan.reduce((s, p) => s + p.take, 0);
  if (drawn < 1) return null;

  const railBlack = new THREE.MeshStandardMaterial({ color: 0x14161a, roughness: 0.5, metalness: 0.55, envMapIntensity: 0.7 });
  const glassOpts = { color: 0xffffff, roughness: 0.12, metalness: 0, envMapIntensity: 1.6 };
  const glass = LOW_POWER ? new THREE.MeshStandardMaterial(glassOpts)
    : new THREE.MeshPhysicalMaterial(Object.assign({ clearcoat: 1, clearcoatRoughness: 0.08, reflectivity: 0.6, ior: 1.5 }, glassOpts));
  const bodies = new THREE.InstancedMesh(bottleGeometry(bottleD, bottleLen, seg), glass, drawn); bodies.castShadow = true; bodies.receiveShadow = true;
  const foilMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.45, metalness: 0.55, envMapIntensity: 1.1 });
  const foils = new THREE.InstancedMesh(foilGeometry(bottleD, bottleLen), foilMat, drawn);
  let labels = null;
  if (isLabel) {
    const lg = new THREE.CylinderGeometry(bottleD * 0.505, bottleD * 0.505, bottleLen * 0.34, LOW_POWER ? 12 : 24, 1, true); lg.rotateX(Math.PI / 2);
    const lm = new THREE.MeshStandardMaterial({ color: 0xffffff, normalMap: mats.paper, normalScale: new THREE.Vector2(0.5, 0.5), roughness: 0.9, metalness: 0, side: THREE.DoubleSide });
    labels = new THREE.InstancedMesh(lg, lm, drawn);
  }

  const palette = [0x143024, 0x1c3a2a, 0x24402c, 0x0f251b, 0x3a2c12, 0x2b1d0f, 0x0d1712, 0x101a13, 0x2a1508];
  const foilCols = [0x9c7b3a, 0x7a1f2b, 0x1c1c1c, 0xb0902f, 0x5a1524, 0xcbb06a, 0x25406b];
  const labelCols = [0xf3ecd9, 0xe8dcc0, 0xf6f1e6, 0xd9c9a3, 0xece4d2, 0xcaa77a, 0xd8d2c4];
  const col = new THREE.Color(), scl = new THREE.Vector3(1, 1, 1), up = new THREE.Vector3(0, 1, 0), Z = new THREE.Vector3(0, 0, 1), down = new THREE.Vector3(0, -1, 0);
  const M = (p, q) => new THREE.Matrix4().compose(p, q, scl);

  let i = 0, idx = 0;
  const railGeoCache = {};
  for (let pi = 0; pi < plan.length; pi++) {
    const P = plan[pi]; if (P.take < 1) continue;
    const b = P.basis;
    const usedCols = Math.min(P.cols, Math.ceil(P.take / rows));
    const spanW = usedCols * hCell, u0 = -spanW / 2 + hCell / 2;
    let qBottle;
    if (isLabel) { // bottle lies ALONG the wall (broadside, label to room), slight upward cant
      const qAlign = new THREE.Quaternion().setFromUnitVectors(Z, b.u);
      qBottle = new THREE.Quaternion().setFromAxisAngle(b.out, 0.06).multiply(qAlign);
    } else { // bottle PERPENDICULAR to wall (capsule to room), neck dipped down
      const qAlign = new THREE.Quaternion().setFromUnitVectors(Z, b.out);
      const sign = new THREE.Vector3().copy(b.u).cross(b.out).dot(down) >= 0 ? 1 : -1;
      qBottle = new THREE.Quaternion().setFromAxisAngle(b.u, 0.16 * sign).multiply(qAlign);
    }
    const filledRowsMax = Math.min(rows, Math.ceil(P.take / usedCols));
    const railH = filledRowsMax * vPitch + bottleD * 0.4;
    const railGeo = railGeoCache[railH] || (railGeoCache[railH] = new THREE.BoxGeometry(0.05, railH, 0.05));
    let placed = 0;
    for (let c = 0; c < usedCols && placed < P.take; c++) {
      const uc = u0 + c * hCell;
      const railPos = new THREE.Vector3().copy(b.plane).addScaledVector(b.u, uc).addScaledVector(up, baseY + railH / 2 - vPitch * 0.2).addScaledVector(b.out, 0.05);
      const rail = new THREE.Mesh(railGeo, railBlack); rail.position.copy(railPos); rail.castShadow = true; group.add(rail);
      for (let r = 0; r < rows && placed < P.take; r++) {
        const y = baseY + r * vPitch + vPitch / 2;
        const cell = new THREE.Vector3().copy(b.plane).addScaledVector(b.u, uc).addScaledVector(up, y);
        if (isLabel) {
          const base = cell.clone().addScaledVector(b.out, bottleD * 0.55).addScaledVector(b.u, -bottleLen * 0.5);
          bodies.setMatrixAt(i, M(base, qBottle)); bodies.setColorAt(i, col.setHex(palette[idx % palette.length]));
          const lp = cell.clone().addScaledVector(b.out, bottleD * 0.55).addScaledVector(b.u, -bottleLen * 0.1);
          labels.setMatrixAt(i, M(lp, qBottle)); labels.setColorAt(i, col.setHex(labelCols[(idx * 5) % labelCols.length]));
          const fp = cell.clone().addScaledVector(b.out, bottleD * 0.55).addScaledVector(b.u, bottleLen * 0.42);
          foils.setMatrixAt(i, M(fp, qBottle)); foils.setColorAt(i, col.setHex(foilCols[(idx * 3) % foilCols.length]));
        } else {
          const base = cell.clone().addScaledVector(b.out, bottleD * 0.5);
          bodies.setMatrixAt(i, M(base, qBottle)); bodies.setColorAt(i, col.setHex(palette[idx % palette.length]));
          const fp = cell.clone().addScaledVector(b.out, bottleLen * 0.82);
          foils.setMatrixAt(i, M(fp, qBottle)); foils.setColorAt(i, col.setHex(foilCols[(idx * 3) % foilCols.length]));
        }
        i++; placed++; idx++;
      }
    }
    const spanH = filledRowsMax * vPitch;
    const proxyPos = new THREE.Vector3().copy(b.plane).addScaledVector(up, baseY + spanH / 2).addScaledVector(b.out, projFt * 0.5);
    const proxyQ = new THREE.Quaternion().setFromUnitVectors(Z, b.out);
    const proxy = new THREE.Mesh(new THREE.BoxGeometry(spanW, spanH, projFt * 1.1), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }));
    proxy.position.copy(proxyPos); proxy.quaternion.copy(proxyQ); proxy.userData.wccLabel = label; group.add(proxy); if (pickables) pickables.push(proxy);
    const cs = new THREE.Mesh(new THREE.PlaneGeometry(spanW * 1.12, projFt * 1.5), new THREE.MeshBasicMaterial({ map: mats.contact, transparent: true, depthWrite: false, opacity: 0.5 }));
    cs.rotation.x = -Math.PI / 2; cs.quaternion.premultiply(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, -1), b.out));
    cs.position.copy(new THREE.Vector3().copy(b.plane).addScaledVector(up, 0.012).addScaledVector(b.out, projFt * 0.5)); group.add(cs);
  }
  bodies.instanceMatrix.needsUpdate = true; if (bodies.instanceColor) bodies.instanceColor.needsUpdate = true;
  foils.instanceMatrix.needsUpdate = true; if (foils.instanceColor) foils.instanceColor.needsUpdate = true;
  group.add(bodies); group.add(foils);
  if (labels) { labels.instanceMatrix.needsUpdate = true; if (labels.instanceColor) labels.instanceColor.needsUpdate = true; group.add(labels); }
  return { shown: drawn, holds: holds, truncated: drawn < holds, wallsUsed: plan.filter((p) => p.take > 0).length, orient: orient };
}

/* ---- environment + orbit ---------------------------------------------- */
function environmentTexture(renderer) {
  const s = new THREE.Scene();
  const panel = (color, intensity, w, h) => { const m = new THREE.MeshStandardMaterial(); m.emissive = new THREE.Color(color); m.emissiveIntensity = intensity; m.color = new THREE.Color(0); m.side = THREE.BackSide; return new THREE.Mesh(new THREE.PlaneGeometry(w, h), m); };
  const boxMat = new THREE.MeshStandardMaterial({ side: THREE.BackSide }); boxMat.emissive = new THREE.Color(0x6a6f78); boxMat.emissiveIntensity = 0.7; s.add(new THREE.Mesh(new THREE.BoxGeometry(30, 22, 30), boxMat));
  const light = panel(0xffffff, 6, 16, 16); light.position.set(0, 10.8, 0); light.rotation.x = Math.PI / 2; s.add(light);
  const warm = panel(0xffe6c0, 3, 10, 12); warm.position.set(-14.5, 2, 0); warm.rotation.y = Math.PI / 2; s.add(warm);
  const cool = panel(0xbcd4ff, 2.2, 10, 12); cool.position.set(14.5, 2, 0); cool.rotation.y = -Math.PI / 2; s.add(cool);
  const pm = new THREE.PMREMGenerator(renderer); const tex = pm.fromScene(s, 0.04).texture; s.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material) o.material.dispose(); }); pm.dispose(); return tex;
}

function overviewTheta(wall) {
  if (wall === 'left') return Math.PI * 0.62;
  if (wall === 'right') return -Math.PI * 0.28;
  return Math.PI * 0.27;
}

function Orbit(camera, dom, target, onChange) {
  this.cam = camera; this.dom = dom; this.target = target.clone(); this.theta = Math.PI * 0.27; this.phi = Math.PI * 0.36; this.radius = 20; this.minR = 3; this.maxR = 120;
  this.tTheta = this.theta; this.tPhi = this.phi; this.tRadius = this.radius; this.dragging = false; this.lx = 0; this.ly = 0;
  const self = this; this._onChange = onChange; const mark = () => { if (self._onChange) self._onChange(); };
  const single = (e) => e.touches ? e.touches[0] : e;
  const dist2 = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  const mmove = (e) => { if (!self.dragging) return; self.tTheta -= (e.clientX - self.lx) * .008; self.tPhi -= (e.clientY - self.ly) * .008; self.tPhi = Math.max(.1, Math.min(Math.PI / 2 - .02, self.tPhi)); self.lx = e.clientX; self.ly = e.clientY; mark(); };
  const mup = () => { self.dragging = false; };
  const mdown = (e) => { self.armed = true; self.dragging = true; self.lx = e.clientX; self.ly = e.clientY; };
  const wheel = (e) => { if (!self.armed) return; self.tRadius *= (e.deltaY > 0 ? 1.08 : .92); self.tRadius = Math.max(self.minR, Math.min(self.maxR, self.tRadius)); if (e.cancelable) e.preventDefault(); mark(); };
  let pinchStart = 0, pinchR = 0, decided = null;
  const tstart = (e) => {
    self.armed = true;
    if (e.touches.length >= 2) { pinchStart = dist2(e.touches[0], e.touches[1]); pinchR = self.tRadius; decided = 'pinch'; self.dragging = false; }
    else { const p = single(e); self.lx = p.clientX; self.ly = p.clientY; self.sx = p.clientX; self.sy = p.clientY; decided = null; self.dragging = true; }
  };
  const tmove = (e) => {
    if (decided === 'pinch' && e.touches.length >= 2) { const d = dist2(e.touches[0], e.touches[1]); if (pinchStart > 0) { self.tRadius = Math.max(self.minR, Math.min(self.maxR, pinchR * pinchStart / d)); mark(); } if (e.cancelable) e.preventDefault(); return; }
    if (!self.dragging) return;
    const p = single(e);
    if (decided === null) { const dx = Math.abs(p.clientX - self.sx), dy = Math.abs(p.clientY - self.sy); if (dx < 6 && dy < 6) return; decided = dx > dy ? 'orbit' : 'scroll'; if (decided === 'scroll') { self.dragging = false; return; } }
    if (decided !== 'orbit') return;
    self.tTheta -= (p.clientX - self.lx) * .008; self.tPhi -= (p.clientY - self.ly) * .008; self.tPhi = Math.max(.1, Math.min(Math.PI / 2 - .02, self.tPhi)); self.lx = p.clientX; self.ly = p.clientY; if (e.cancelable) e.preventDefault(); mark();
  };
  const tend = (e) => { if (!e.touches || e.touches.length === 0) { self.dragging = false; decided = null; } };
  dom.addEventListener('mousedown', mdown); window.addEventListener('mousemove', mmove); window.addEventListener('mouseup', mup);
  dom.addEventListener('touchstart', tstart, { passive: true }); dom.addEventListener('touchmove', tmove, { passive: false }); dom.addEventListener('touchend', tend); dom.addEventListener('touchcancel', tend);
  dom.addEventListener('wheel', wheel, { passive: false });
  this._teardown = () => { window.removeEventListener('mousemove', mmove); window.removeEventListener('mouseup', mup); };
}
Orbit.prototype.frame = function (dims, name, wall) {
  const big = Math.max(dims.L, dims.W, dims.H);
  if (name === 'top') { this.tTheta = Math.PI * .25; this.tPhi = .16; this.tRadius = big * 1.7; }
  else if (name === 'door') { this.tTheta = 0; this.tPhi = Math.PI * .42; this.tRadius = big * 1.5; }
  else if (name === 'racks') { this.tTheta = overviewTheta(wall); this.tPhi = Math.PI * .38; this.tRadius = big * 1.15; }
  else { this.tTheta = overviewTheta(wall); this.tPhi = Math.PI * .36; this.tRadius = big * 1.7; }
  this.minR = big * .35; this.maxR = big * 3;
};
Orbit.prototype.update = function () {
  const k = REDUCED_MOTION ? 1 : .12;
  const moving = Math.abs(this.tTheta - this.theta) + Math.abs(this.tPhi - this.phi) + Math.abs(this.tRadius - this.radius) > 1e-3;
  this.theta += (this.tTheta - this.theta) * k; this.phi += (this.tPhi - this.phi) * k; this.radius += (this.tRadius - this.radius) * k;
  const t = this.target, r = this.radius;
  this.cam.position.set(t.x + r * Math.sin(this.phi) * Math.sin(this.theta), t.y + r * Math.cos(this.phi), t.z + r * Math.sin(this.phi) * Math.cos(this.theta)); this.cam.lookAt(t.x, t.y, t.z);
  return moving;
};

/* ---- lifecycle -------------------------------------------------------- */
let S = null;
const state = { unitWall: 'left', lastInput: null, lastQuote: null, lastMount: null, lastForm: null, lastCat: null };

function fullTeardown() {
  if (!S) return;
  cancelAnimationFrame(S.raf);
  if (S.io) S.io.disconnect();
  if (S.orbit && S.orbit._teardown) S.orbit._teardown();
  if (S.onResize) window.removeEventListener('resize', S.onResize);
  disposeScene(S.scene);
  if (S.env) S.env.dispose();
  if (S.mats) Object.keys(S.mats).forEach((k) => { const m = S.mats[k]; if (m && m.map) m.map.dispose(); if (m && m.normal) m.normal.dispose(); if (m && m.dispose) m.dispose(); });
  if (S.renderer) { S.renderer.dispose(); if (S.renderer.domElement && S.renderer.domElement.parentNode) S.renderer.domElement.remove(); }
  S = null;
}
function disposeScene(root) {
  if (!root) return;
  root.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    const mats = Array.isArray(o.material) ? o.material : (o.material ? [o.material] : []);
    mats.forEach((m) => { if (m && !m.userData.wccShared) m.dispose(); });
  });
}

/* which walls racking may use: never the unit's wall, never the front (door) */
function rackWallsFor(unitWall) {
  const order = ['back', 'left', 'right'];
  return order.filter((w) => w !== unitWall);
}

function render(mountEl, input, quote) {
  if (!mountEl || !input.length || !input.width || !input.height) return null;
  const L = input.length, W = input.width, H = input.height;
  const stageW = mountEl.clientWidth || 640, stageH = mountEl.clientHeight || 460;
  const reuse = S && S.mountEl === mountEl && S.renderer && !S.contextLost;
  const keepCam = reuse;

  let renderer, scene, camera, mats, env, orbit;
  if (reuse) {
    renderer = S.renderer; mats = S.mats; env = S.env;
    disposeScene(S.scene);
  } else {
    fullTeardown();
    renderer = new THREE.WebGLRenderer({ antialias: !LOW_POWER, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, LOW_POWER ? 1.5 : 2));
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = LOW_POWER ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.08; renderer.outputColorSpace = THREE.SRGBColorSpace;
    mountEl.innerHTML = ''; mountEl.appendChild(renderer.domElement);
    mats = buildMats();
    env = environmentTexture(renderer);
  }
  renderer.setSize(stageW, stageH);

  scene = new THREE.Scene(); scene.background = new THREE.Color(0x15181c); scene.environment = env;
  camera = new THREE.PerspectiveCamera(48, stageW / stageH, 0.05, 500);
  const key = new THREE.DirectionalLight(0xfff2de, 2.2); key.position.set(L * 0.7, H * 1.7, W * 1.0); key.castShadow = true;
  const sm = LOW_POWER ? 1024 : 2048; key.shadow.mapSize.set(sm, sm); key.shadow.bias = -0.0004;
  const d = Math.max(L, W, H) * 1.5; Object.assign(key.shadow.camera, { left: -d, right: d, top: d, bottom: -d, near: 0.5, far: d * 4 }); key.shadow.camera.updateProjectionMatrix();
  scene.add(key); scene.add(new THREE.DirectionalLight(0xbfd4ff, 0.35));

  const room = new THREE.Group(); scene.add(room);
  const walls = buildRoom(room, L, W, H, mats, { glass: input.glass, standardDoor: input.standardDoor });
  const ceiling = room.children.find((o) => o.userData && o.userData.isCeiling);
  const pickables = [];
  const money = (n) => '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const imgTag = (src) => src ? `<img src="${esc(thumb(src, 340))}" style="width:100%;max-width:170px;height:auto;display:block;border-radius:5px;background:#fff;margin:0 0 7px" alt="">` : '';
  const link = (url) => url ? `<a href="${esc(url)}" target="_blank" rel="noopener" style="display:inline-block;margin-top:6px;color:#8fd0ff;font-weight:600;text-decoration:none">View product &rarr;</a>` : '';

  // ---- units (multi-unit aware) ----
  const unitPick = quote.picks && quote.picks[0];
  let unitMeta = null;
  if (unitPick) {
    const u = unitPick.unit, qty = Math.max(1, unitPick.qty || 1);
    const uLabel = imgTag(u.img) + `<b>${esc(u.title)}</b><br>${esc(u.install || u.vendor)} &middot; rated ${Number(u.cuft).toLocaleString()} cu ft each`
      + (qty > 1 ? ` &middot; ${qty} units` : '')
      + (unitPick.variant ? `<br>${money(unitPick.variant.price)}${qty > 1 ? ' each' : ''}` : '')
      + (loadBtu(quote) ? `<br><span style="color:#bcd">Sized for ~${loadBtu(quote).toLocaleString()} BTU/h load</span>` : '')
      + (u.tech ? '<br><span style="color:#ffd27a">Requires an HVAC-R technician</span>' : '')
      + link(u.url);
    const wallLen = state.unitWall === 'back' ? L : W;
    // estimate one unit width to space them
    const fwEst = Math.min((u.dim && u.dim.w ? u.dim.w / 12 : 1.5), wallLen * 0.4);
    const gap = Math.max(0.3, fwEst * 0.4);
    const span = qty * fwEst + (qty - 1) * gap;
    const start = -Math.min(span, wallLen * 0.92) / 2 + fwEst / 2;
    const stepU = qty > 1 ? (Math.min(span, wallLen * 0.92) - fwEst) / (qty - 1) : 0;
    for (let k2 = 0; k2 < qty; k2++) {
      const off = qty > 1 ? start + k2 * stepU : 0;
      const m = buildUnit(room, L, W, H, mats, u, state.unitWall, off, uLabel, pickables);
      if (!unitMeta) unitMeta = m;
    }
  }

  // ---- racking (real count, multi-wall wrap) ----
  const rackPick = quote.racks && quote.racks[0];
  const holds = rackPick && input.bottles ? Math.min(input.bottles, rackPick.holds || input.bottles) : 0;
  const rLabel = rackPick ? imgTag(rackPick.rack.img) + `<b>${esc(rackPick.rack.title)}</b><br>${holds.toLocaleString()} bottles &middot; ${rackPick.qty} racks &middot; ${money(rackPick.total)}` + link(rackPick.rack.url) : null;
  const maxDraw = LOW_POWER ? 500 : 1100;
  const rackWalls = rackWallsFor(state.unitWall);
  const rackInfo = holds ? buildRacks(room, L, W, H, holds, mats, rackWalls, rLabel, pickables, maxDraw, rackPick.rack.dim) : null;

  // ---- camera / orbit ----
  const needsRenderRef = { v: true };
  const markDirty = () => { needsRenderRef.v = true; };
  if (reuse && S.orbit) { orbit = S.orbit; orbit.cam = camera; orbit.dom = renderer.domElement; orbit._onChange = markDirty; }
  else { orbit = new Orbit(camera, renderer.domElement, new THREE.Vector3(0, H * 0.45, 0), markDirty); }
  if (!keepCam) { orbit.frame({ L, W, H }, 'overview', state.unitWall); orbit.theta = orbit.tTheta; orbit.phi = orbit.tPhi; orbit.radius = orbit.tRadius; }

  const prevRaf = S ? S.raf : 0;
  S = Object.assign(S || {}, { renderer, scene, camera, orbit, env, mats, dims: { L, W, H }, mountEl, walls, pickables, visible: true, needsRender: needsRenderRef, contextLost: false });
  if (prevRaf) cancelAnimationFrame(prevRaf);
  const myGen = (S._gen = (S._gen || 0) + 1); // only the latest loop generation runs; stale loops self-stop

  const bigLW = Math.max(L, W);
  const cutaway = () => {
    for (let i = 0; i < walls.length; i++) {
      const w = walls[i]; const toCam = camera.position.clone().sub(w.position);
      const outside = w.userData.normal.dot(toCam) > bigLW * 0.02;
      const op = outside ? 0 : 1; if (w.material.opacity !== op) { w.material.opacity = op; markDirty(); } w.visible = op > 0.001 || !outside;
    }
    // hide the ceiling when looking steeply down (top-down / high angle) so you see the room, not the roof
    if (ceiling) { const cv = orbit.phi > 0.55; if (ceiling.visible !== cv) { ceiling.visible = cv; markDirty(); } }
  };

  const loop = () => {
    if (!S || S._gen !== myGen) return; // teardown or a newer render superseded us — stop
    S.raf = requestAnimationFrame(loop);
    if (!S.visible || S.contextLost) return;
    const moving = orbit.update();
    cutaway();
    if (S.needsRender.v || moving) { renderer.render(scene, camera); S.needsRender.v = false; }
    if (S.pendingPointer && !orbit.dragging) { doPick(S.pendingPointer); S.pendingPointer = null; }
  };
  loop();

  if (!reuse) {
    S.onResize = () => { const w = mountEl.clientWidth || stageW, h = mountEl.clientHeight || stageH; camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h); markDirty(); };
    window.addEventListener('resize', S.onResize);
    try { S.io = new IntersectionObserver((es) => { es.forEach((e) => { S.visible = e.isIntersecting; if (S.visible) markDirty(); }); }, { threshold: 0.01 }); S.io.observe(mountEl); } catch (e) { S.visible = true; }
    renderer.domElement.addEventListener('webglcontextlost', (e) => { e.preventDefault(); S.contextLost = true; warn('context lost'); });
    renderer.domElement.addEventListener('webglcontextrestored', () => { warn('context restored'); S.contextLost = false; if (state.lastMount && state.lastInput && state.lastQuote) render(state.lastMount, state.lastInput, state.lastQuote); });
  } else { S.camera = camera; if (S.onResize) S.onResize(); }

  // ---- picking + tooltip ----
  const ray = new THREE.Raycaster(); const ndc = new THREE.Vector2();
  let tip = mountEl.querySelector('.wcc3d-tip');
  if (!tip) {
    tip = document.createElement('div'); tip.className = 'wcc3d-tip';
    tip.style.cssText = 'position:absolute;z-index:5;background:rgba(18,20,23,.95);color:#fff;font:12px/1.45 system-ui,-apple-system,sans-serif;padding:8px 11px;border-radius:9px;max-width:250px;box-shadow:0 6px 20px rgba(0,0,0,.4);opacity:0;transition:opacity .09s;transform:translate(-50%,-118%);white-space:normal;pointer-events:none';
    mountEl.style.position = 'relative'; mountEl.appendChild(tip);
  }
  let live = mountEl.querySelector('.wcc3d-live');
  if (!live) { live = document.createElement('div'); live.className = 'wcc3d-live'; live.setAttribute('aria-live', 'polite'); live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap'; mountEl.appendChild(live); }
  S.tip = tip; S.pinned = false;
  const labelAt = (obj) => { let o = obj; while (o) { if (o.userData && o.userData.wccLabel) return o.userData.wccLabel; o = o.parent; } return null; };
  function pickLabel(clientX, clientY) {
    const rect = renderer.domElement.getBoundingClientRect();
    const cx = clientX - rect.left, cy = clientY - rect.top;
    ndc.x = (cx / rect.width) * 2 - 1; ndc.y = -(cy / rect.height) * 2 + 1;
    ray.setFromCamera(ndc, camera);
    const hits = ray.intersectObjects(pickables, false);
    return { lab: hits.length ? labelAt(hits[0].object) : null, cx, cy };
  }
  function showTip(lab, cx, cy, pinned) {
    if (S._lastLab !== lab) { tip.innerHTML = lab; S._lastLab = lab; live.textContent = tip.textContent; }
    const rect = renderer.domElement.getBoundingClientRect();
    const tw = tip.offsetWidth || 200, th = tip.offsetHeight || 90;
    const above = cy - th - 14 > 0; // flip below the cursor when there's no room above
    tip.style.transform = 'translate(-50%,' + (above ? '-118%' : '20%') + ')';
    const left = Math.max(tw / 2 + 6, Math.min(rect.width - tw / 2 - 6, cx));
    const top = Math.max(6, Math.min(rect.height - 6, cy));
    tip.style.left = left + 'px'; tip.style.top = top + 'px';
    tip.style.opacity = '1'; tip.style.pointerEvents = pinned ? 'auto' : 'none'; S.pinned = !!pinned;
  }
  function hideTip() { tip.style.opacity = '0'; tip.style.pointerEvents = 'none'; S.pinned = false; S._lastLab = null; }
  function doPick(pt) { if (S.pinned) return; const r = pickLabel(pt.x, pt.y); if (r.lab) { showTip(r.lab, r.cx, r.cy, false); renderer.domElement.style.cursor = 'pointer'; } else { hideTip(); renderer.domElement.style.cursor = 'grab'; } }
  S.onMove = (ev) => { if (ev.pointerType === 'touch') return; S.pendingPointer = { x: ev.clientX, y: ev.clientY }; };
  S.onLeave = () => { if (!S.pinned) hideTip(); };
  let downXY = null;
  S.onDown = (ev) => { downXY = { x: ev.clientX, y: ev.clientY }; };
  S.onUp = (ev) => { if (!downXY) return; const moved = Math.hypot(ev.clientX - downXY.x, ev.clientY - downXY.y); downXY = null; if (moved > 8) return; const r = pickLabel(ev.clientX, ev.clientY); if (r.lab) showTip(r.lab, r.cx, r.cy, true); else hideTip(); };
  const dom = renderer.domElement;
  dom.addEventListener('pointermove', S.onMove); dom.addEventListener('pointerleave', S.onLeave); dom.addEventListener('pointerdown', S.onDown); dom.addEventListener('pointerup', S.onUp);

  // keyboard accessibility: focusable canvas, arrows orbit, +/- zoom, Esc closes pinned tip
  dom.setAttribute('tabindex', '0'); dom.setAttribute('role', 'application');
  dom.setAttribute('aria-label', 'Interactive 3D cellar. Arrow keys orbit, plus and minus zoom, Escape closes a pinned label.');
  S.onKey = (e) => {
    let h = false; const o = orbit;
    if (e.key === 'ArrowLeft') { o.tTheta -= 0.12; h = 1; } else if (e.key === 'ArrowRight') { o.tTheta += 0.12; h = 1; }
    else if (e.key === 'ArrowUp') { o.tPhi = Math.max(.1, o.tPhi - 0.08); h = 1; } else if (e.key === 'ArrowDown') { o.tPhi = Math.min(Math.PI / 2 - .02, o.tPhi + 0.08); h = 1; }
    else if (e.key === '+' || e.key === '=') { o.tRadius = Math.max(o.minR, o.tRadius * 0.9); h = 1; } else if (e.key === '-' || e.key === '_') { o.tRadius = Math.min(o.maxR, o.tRadius * 1.1); h = 1; }
    else if (e.key === 'Escape') { hideTip(); }
    if (h) { e.preventDefault(); o.armed = true; markDirty(); }
  };
  dom.addEventListener('keydown', S.onKey);

  // idle "drag to look" hint chip, fades on first interaction
  let hint = mountEl.querySelector('.wcc3d-hint-chip');
  if (!hint) {
    hint = document.createElement('div'); hint.className = 'wcc3d-hint-chip'; hint.textContent = 'Drag to look · scroll to zoom';
    hint.style.cssText = 'position:absolute;left:50%;bottom:12px;transform:translateX(-50%);z-index:5;background:rgba(15,20,26,.72);color:#fff;font:12px system-ui;padding:6px 12px;border-radius:20px;pointer-events:none;transition:opacity .4s;backdrop-filter:blur(4px)';
    mountEl.appendChild(hint);
    const kill = () => { hint.style.opacity = '0'; setTimeout(() => { if (hint && hint.parentNode) hint.remove(); }, 420); dom.removeEventListener('pointerdown', kill); dom.removeEventListener('wheel', kill); };
    dom.addEventListener('pointerdown', kill); dom.addEventListener('wheel', kill, { passive: true }); setTimeout(kill, 6000);
  }
  markDirty();

  return { rackInfo, unit: unitPick };
}

/* capture the current view + a top-down as data URLs (sync — preserveDrawingBuffer) */
function captureViews() {
  if (!S || !S.renderer) return null;
  const shots = {};
  try {
    shots.current = S.renderer.domElement.toDataURL('image/jpeg', 0.85);
    const o = S.orbit, save = { th: o.theta, ph: o.phi, r: o.radius };
    o.theta = Math.PI * .25; o.phi = .16; o.radius = Math.max(S.dims.L, S.dims.W, S.dims.H) * 1.7;
    o.cam.position.set(o.target.x + o.radius * Math.sin(o.phi) * Math.sin(o.theta), o.target.y + o.radius * Math.cos(o.phi), o.target.z + o.radius * Math.sin(o.phi) * Math.cos(o.theta)); o.cam.lookAt(o.target.x, o.target.y, o.target.z);
    for (let i = 0; i < S.walls.length; i++) { S.walls[i].material.opacity = S.walls[i].userData.normal.dot(o.cam.position.clone().sub(S.walls[i].position)) > 0 ? 0 : 1; }
    S.renderer.render(S.scene, S.cam || S.camera); shots.top = S.renderer.domElement.toDataURL('image/jpeg', 0.85);
    o.theta = save.th; o.phi = save.ph; o.radius = save.r; S.needsRender.v = true;
  } catch (e) { warn('capture failed', e); }
  return shots;
}

function setPreset(name) { if (S) { const wall = name === 'racks' ? (rackWallsFor(state.unitWall)[0] || state.unitWall) : state.unitWall; S.orbit.frame(S.dims, name, wall); S.needsRender.v = true; highlightPreset(name); } }
function highlightPreset(name) { const bar = document.getElementById('WccQuote3DBar'); if (bar) [].forEach.call(bar.querySelectorAll('[data-view]'), (b) => { const on = b.getAttribute('data-view') === name; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); }); }
function setUnitWall(wall) {
  if (['left', 'right', 'back'].indexOf(wall) < 0) return;
  state.unitWall = wall;
  const bar = document.getElementById('WccQuote3DWalls');
  if (bar) [].forEach.call(bar.querySelectorAll('[data-wall]'), (b) => { const on = b.getAttribute('data-wall') === wall; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
  if (state.lastMount && state.lastInput && state.lastQuote) {
    if (currentView === '3d') {
      const meta = render(state.lastMount, state.lastInput, state.lastQuote);
      if (S) { S.orbit.frame(S.dims, 'overview', wall); highlightPreset('overview'); }
      updateCaption(meta, state.lastQuote, state.lastCat);
    } else {
      const p = renderBlueprint(state.lastMount, state.lastInput, state.lastQuote, state.lastCat);
      state.lastPlanInfo = p && p.planRacks ? { shown: p.planRacks.shown, holds: p.holds, truncated: p.planRacks.truncated, wallsUsed: p.planRacks.dist.filter((d) => d.count > 0).length } : null;
      injectToggle(state.lastMount); updateToggle(state.lastMount);
      updateCaption({ rackInfo: state.lastPlanInfo }, state.lastQuote, state.lastCat);
    }
    if (state.lastForm) syncUrl(state.lastForm, wall);
  }
}
function wireBar() { const bar = document.getElementById('WccQuote3DBar'); if (bar && !bar._w) { bar._w = 1; bar.addEventListener('click', (e) => { const b = e.target.closest('[data-view]'); if (b) setPreset(b.getAttribute('data-view')); }); highlightPreset('overview'); } }
function wireWalls() { const bar = document.getElementById('WccQuote3DWalls'); if (bar && !bar._w) { bar._w = 1; bar.addEventListener('click', (e) => { const b = e.target.closest('[data-wall]'); if (b) setUnitWall(b.getAttribute('data-wall')); }); [].forEach.call(bar.querySelectorAll('[data-wall]'), (b) => { const on = b.getAttribute('data-wall') === state.unitWall; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); }); } }

function fallbackCard(wrap, mountEl) {
  if (!mountEl) return;
  mountEl.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;padding:24px;text-align:center;color:#aeb4bb;font:14px/1.5 system-ui,-apple-system,sans-serif">3D preview isn’t available in this browser — your quote above is unaffected.</div>';
  if (wrap && wrap.style) wrap.style.display = '';
}

function updateCaption(meta, quote, cat) {
  const cap = document.getElementById('WccQuote3DCap'); if (!cap) return;
  const ri = meta && meta.rackInfo;
  const rackTitle = (quote.racks && quote.racks[0] && quote.racks[0].rack.title) || 'the recommended rack';
  const phone = (cat && cat.phone || '').toString();
  if (ri && ri.truncated) cap.textContent = `Showing ${ri.shown.toLocaleString()} of your ${ri.holds.toLocaleString()} bottles to scale across ${ri.wallsUsed} wall${ri.wallsUsed > 1 ? 's' : ''} — your full quote is above${phone ? '. Call ' + phone + ' and we’ll plan racking for the whole collection.' : '.'}`;
  else if (ri) cap.textContent = `${ri.shown.toLocaleString()} bottles shown to scale across ${ri.wallsUsed} wall${ri.wallsUsed > 1 ? 's' : ''} on ${rackTitle}.`;
  else cap.textContent = 'Room and cooling unit shown to scale. Add a bottle count to fill the racks.';
}

function syncUrl(form, wall) {
  try { if (location.pathname.indexOf('calculator') < 0) return; history.replaceState(null, '', shareUrl(form, wall)); } catch (e) {}
}

/* share bar (spec sheet + email + copy link), injected once per wrap */
function buildShareBar(wrap, form, quote, cat) {
  if (!wrap) return;
  let bar = wrap.querySelector('.calc-3d-share');
  if (!bar) {
    bar = document.createElement('div'); bar.className = 'calc-3d-share';
    bar.style.cssText = 'max-width:640px;margin:14px auto 0;background:#f7f5f0;border:1px solid #e6e0d4;border-radius:12px;padding:14px 16px;text-align:center';
    bar.innerHTML = '<div style="font:700 1.02rem/1.3 system-ui,sans-serif;color:#2a2118;margin-bottom:2px">Email yourself this cellar plan</div>'
      + '<div style="font:.86rem/1.4 system-ui,sans-serif;color:#6b6154;margin-bottom:10px">Get the spec sheet with your quote &mdash; and a specialist can follow up if you’d like.</div>'
      + '<div style="display:flex;gap:8px;flex-wrap:wrap;justify-content:center">'
      + '<input type="email" class="calc-3d-email" placeholder="you@example.com" autocomplete="email" aria-label="Your email" style="flex:1 1 240px;min-height:44px;padding:.5rem .8rem;border:1px solid #d9cfb8;border-radius:9px;font:1rem system-ui,sans-serif;background:#fff">'
      + '<button type="button" class="calc-3d-btn" data-share="lead" style="min-height:44px;background:#8a6d3b">Email it to me</button>'
      + '</div>'
      + '<label style="display:inline-flex;gap:7px;align-items:center;font:.82rem system-ui,sans-serif;color:#6b6154;margin-top:9px;cursor:pointer"><input type="checkbox" class="calc-3d-optin">Also email me expert tips &amp; occasional offers</label>'
      + '<div class="calc-3d-leadmsg" role="status" aria-live="polite" style="font:.85rem system-ui,sans-serif;min-height:1.1em;margin-top:7px"></div>'
      + '<div style="display:flex;gap:8px;flex-wrap:wrap;justify-content:center;margin-top:4px">'
      + '<button type="button" class="calc-3d-btn" data-share="spec">Download spec sheet</button>'
      + '<button type="button" class="calc-3d-btn" data-share="copy">Copy shareable link</button>'
      + '</div>';
    const cap = wrap.querySelector('.calc-3d-cap'); if (cap && cap.parentNode) cap.parentNode.insertBefore(bar, cap.nextSibling); else wrap.appendChild(bar);
    bar.addEventListener('click', (e) => {
      const b = e.target.closest('[data-share]'); if (!b) return; const ctx = bar._ctx || {}; const a = b.getAttribute('data-share');
      if (a === 'spec') openSpecSheet(ctx.form, ctx.quote, ctx.cat);
      else if (a === 'copy') copyLink(ctx.form, b);
      else if (a === 'lead') submitLead(bar);
    });
    bar.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target.classList && e.target.classList.contains('calc-3d-email')) { e.preventDefault(); submitLead(bar); } });
  }
  bar._ctx = { form, quote, cat };
}
function submitLead(bar) {
  const ctx = bar._ctx || {};
  const emailEl = bar.querySelector('.calc-3d-email'), msg = bar.querySelector('.calc-3d-leadmsg'), optInEl = bar.querySelector('.calc-3d-optin');
  const email = ((emailEl && emailEl.value) || '').trim();
  if (!validEmail(email)) { if (msg) { msg.style.color = '#8c1c1c'; msg.textContent = 'Please enter a valid email.'; } if (emailEl) emailEl.focus(); return; }
  const optIn = !!(optInEl && optInEl.checked);
  captureLead(email, state.lastInput, ctx.quote || state.lastQuote, { lead_source: 'Cellar Showroom' }, optIn);
  if (msg) { msg.style.color = '#2c6e49'; msg.textContent = optIn ? 'Saved — opening your spec sheet. Check your inbox to confirm your subscription.' : 'Saved — opening your spec sheet.'; }
  openSpecSheet(ctx.form || state.lastForm, ctx.quote || state.lastQuote, ctx.cat || state.lastCat);
}
function copyLink(form, btn) {
  const url = shareUrl(form, state.unitWall);
  const done = () => { const t = btn.textContent; btn.textContent = 'Link copied ✓'; setTimeout(() => { btn.textContent = t; }, 1600); };
  try { navigator.clipboard.writeText(url).then(done, () => { window.prompt('Copy this link', url); }); } catch (e) { window.prompt('Copy this link', url); }
}
/* load an image URL and return a PNG data URI (so it embeds in the printable);
 * rejects on error, timeout, or a CORS taint */
function toDataURI(url) {
  return new Promise((res, rej) => {
    const im = new Image(); im.crossOrigin = 'anonymous';
    im.onload = () => { try { const c = document.createElement('canvas'); c.width = im.naturalWidth; c.height = im.naturalHeight; c.getContext('2d').drawImage(im, 0, 0); res(c.toDataURL('image/png')); } catch (e) { rej(e); } };
    im.onerror = () => rej(new Error('img error'));
    setTimeout(() => rej(new Error('timeout')), 4000);
    im.src = url;
  });
}
function openSpecSheet(form, quote, cat) {
  const w = window.open('', '_blank'); // open in the click gesture so it isn't blocked
  if (!w) { warn('popup blocked'); return; }
  try { w.document.write('<!doctype html><meta charset="utf-8"><title>Cellar Spec Sheet</title><body style="font:16px system-ui;color:#555;padding:26px">Generating spec sheet…</body>'); } catch (e) {}

  const inp = readInput(form);
  const imgs = [];
  try { if (planCanvas) imgs.push({ src: planCanvas.toDataURL('image/jpeg', 0.9), cap: 'Floor plan' }); } catch (e) {}
  if (S && S.renderer) { const v = captureViews() || {}; if (v.current) imgs.push({ src: v.current, cap: '3D view' }); if (v.top && imgs.length < 3) imgs.push({ src: v.top, cap: 'Top-down' }); }
  const money = (n) => '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const shop = (cat && cat.shop) || 'Wine Cooler Collection';
  const phone = (cat && cat.phone) || '';
  let logo = (cat && cat.logo) || '';
  if (logo && logo.indexOf('//') === 0) logo = 'https:' + logo;
  const url = shareUrl(form, state.unitWall);
  const facts = installFacts(inp, quote);
  const rowU = (quote.picks || []).map((p) => `<tr><td>${esc(p.unit.title)}</td><td>${p.qty}</td><td>${money(p.variant ? p.variant.price : 0)}</td><td>${money(p.total)}</td></tr>`).join('');
  const rowR = (quote.racks || []).slice(0, 1).map((r) => `<tr><td>${esc(r.rack.title)}</td><td>${r.qty}</td><td>${money(r.variant ? r.variant.price : 0)}</td><td>${money(r.total)}</td></tr>`).join('');
  const allow = (quote.allowances || []).map((a) => esc(a)).join(', ');
  const img = (o) => o && o.src ? `<figure style="margin:0"><img src="${o.src}" style="width:100%;border:1px solid #ddd;border-radius:6px"><figcaption style="font-size:12px;color:#666;text-align:center;margin-top:4px">${esc(o.cap)}</figcaption></figure>` : '';
  const imgGrid = imgs.map(img).join('');
  const mk = (logoSrc) => `<!doctype html><html><head><meta charset="utf-8"><title>Cellar Spec Sheet — ${esc(shop)}</title>
<style>body{font:14px/1.5 system-ui,-apple-system,sans-serif;color:#222;max-width:820px;margin:24px auto;padding:0 20px}
h1{font-size:22px;margin:0 0 2px}.sub{color:#666;margin:0 0 18px}
table{width:100%;border-collapse:collapse;margin:8px 0 16px}th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #eee}th{background:#f6f4ef}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:12px 0}
.facts td:first-child{color:#666;width:45%}.disc{font-size:12px;color:#7a6a45;background:#f6f1e4;border:1px solid #e6dcc4;border-radius:8px;padding:8px 12px;margin-top:16px}
.hd{display:flex;align-items:center;gap:14px;margin-bottom:4px}.hd img{height:56px;width:auto}
@media print{.noprint{display:none}}</style></head><body>
<div class="hd">${logoSrc ? '<img src="' + logoSrc + '" alt="' + esc(shop) + '">' : ''}<h1 style="margin:0">${esc(shop)} — Cellar Spec Sheet</h1></div>
<p class="sub">To-scale schematic generated from your measurements. For sizing only — confirm specs before ordering.${phone ? ' &middot; ' + esc(phone) : ''}</p>
<div class="grid">${imgGrid}</div>
<h3>Room</h3>
<table class="facts"><tr><td>Dimensions</td><td>${inp.length} × ${inp.width} × ${inp.height} ft</td></tr>
<tr><td>Volume</td><td>${(quote.volume || 0).toLocaleString()} cu ft</td></tr>
<tr><td>Sizing volume</td><td>${(quote.sizingVolume || 0).toLocaleString()} cu ft${allow ? ' (' + allow + ')' : ''}</td></tr>
<tr><td>Estimated load</td><td>${loadBtu(quote).toLocaleString()} BTU/h</td></tr>
<tr><td>Bottles</td><td>${(inp.bottles || 0).toLocaleString()}</td></tr></table>
<h3>Recommended equipment</h3>
<table><thead><tr><th>Item</th><th>Qty</th><th>Unit</th><th>Total</th></tr></thead><tbody>${rowU}${rowR}</tbody></table>
<h3>Installation notes (for your contractor)</h3>
<ul style="margin:6px 0 14px;padding-left:20px;line-height:1.5">
<li><b>Electrical:</b> ${esc(facts.electrical)}</li>
<li><b>Heat rejection:</b> ${esc(facts.heat)}</li>
<li><b>Condensate:</b> ${esc(facts.condensate)}</li>
<li><b>Mounting:</b> ${esc(facts.mount)}</li>
<li><b>Envelope:</b> ${esc(facts.envelope)}</li>
<li><b>Load / sizing:</b> ~${loadBtu(quote).toLocaleString()} BTU/h at ΔT ${facts.deltaT}°F${facts.headroom != null ? '; recommended unit carries ~' + facts.headroom + '% capacity headroom' : ''}.</li>
</ul>
<p><a href="${esc(url)}">Open the interactive 3D &amp; full quote</a></p>
<p class="disc">This is a to-scale schematic, not an exact product rendering. Rack layout is approximate; final capacity depends on the racking chosen. Confirm all specifications with ${esc(shop)} before ordering.</p>
<p class="noprint"><button onclick="window.print()">Print / Save as PDF</button></p>
</body></html>`;
  const write = (logoSrc) => { try { w.document.open(); w.document.write(mk(logoSrc)); w.document.close(); } catch (e) { warn('spec write failed', e); } };
  // embed the logo as a data URI so it always renders + prints; fall back to the plain URL, then to no logo
  if (logo) toDataURI(logo).then(write, () => write(logo)); else write('');
}

/* ---- blueprint (2D floor plan — default view, no WebGL) --------------- */
let planCanvas = null, planResizeBound = false;
function ensurePlanResize() {
  if (planResizeBound) return; planResizeBound = true;
  let t = null;
  window.addEventListener('resize', () => {
    if (currentView !== 'plan' || !state.lastMount || !state.lastInput) return;
    clearTimeout(t); t = setTimeout(() => { if (currentView === 'plan') { renderBlueprint(state.lastMount, state.lastInput, state.lastQuote, state.lastCat); injectToggle(state.lastMount); updateToggle(state.lastMount); } }, 120);
  });
}
function planRackDist(holds, rackWalls, L, W, H, dim) {
  const isLabel = !dim || dim.orient === 'label';
  const inFt = (v, d) => (v ? v / 12 : d);
  const hCell = isLabel ? inFt(dim && dim.colW, 1.083) : inFt(dim && dim.colW, 0.375);
  const vPitch = inFt(dim && dim.pitch, 0.333);
  const rows = Math.max(1, Math.floor((H - 1) / vPitch));
  const dist = []; let rem = holds;
  for (let i = 0; i < rackWalls.length && rem > 0; i++) {
    const wall = rackWalls[i]; const width = (wall === 'back') ? L : W;
    const cols = Math.max(1, Math.floor((width * 0.9) / hCell));
    const take = Math.min(rem, cols * rows); dist.push({ wall: wall, count: take }); rem -= take;
  }
  return { dist: dist, shown: holds - rem, truncated: rem > 0 };
}
function renderBlueprint(mountEl, input, quote, cat) {
  ensurePlanResize();
  const L = input.length, W = input.width, H = input.height;
  let cv = mountEl.querySelector('canvas.wcc3d-plan');
  if (!cv) { cv = document.createElement('canvas'); cv.className = 'wcc3d-plan'; cv.style.cssText = 'display:block;width:100%;height:100%'; }
  mountEl.style.position = 'relative';
  const tog = mountEl.querySelector('.wcc3d-toggle'); mountEl.innerHTML = ''; mountEl.appendChild(cv); if (tog) mountEl.appendChild(tog);
  planCanvas = cv;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cw = mountEl.clientWidth || 640, ch = mountEl.clientHeight || 460;
  cv.width = cw * dpr; cv.height = ch * dpr;
  const x = cv.getContext('2d'); x.setTransform(dpr, 0, 0, dpr, 0, 0);
  const INK = '#dce9ff', FAINT = 'rgba(255,255,255,0.10)', CYAN = '#7fe0ff', AMBER = '#ffcf7a';
  x.fillStyle = '#0f2f5e'; x.fillRect(0, 0, cw, ch);
  const narrow = cw < 520; // phones: drop the side title block, give the plan full width
  const mL = narrow ? 46 : 62, mR = narrow ? 22 : Math.min(210, cw * 0.28), mT = narrow ? 48 : 54, mB = 54;
  const availW = Math.max(40, cw - mL - mR), availH = Math.max(40, ch - mT - mB);
  const scale = Math.min(availW / L, availH / W);
  const rw = L * scale, rh = W * scale;
  const ox = mL + (availW - rw) / 2, oy = mT + (availH - rh) / 2;
  x.font = '12px system-ui,-apple-system,sans-serif'; x.textBaseline = 'middle';
  // scaled grid, aligned to the room origin (each square a whole foot increment,
  // so the room's near corner and — for whole-foot rooms — its walls fall on lines)
  let gridFt = 10; [1, 2, 3, 5, 10].some((n) => { if (n * scale >= 20) { gridFt = n; return true; } return false; });
  const gcell = gridFt * scale;
  x.strokeStyle = FAINT; x.lineWidth = 1; x.beginPath();
  for (let gx = ox; gx <= cw; gx += gcell) { x.moveTo(gx, 0); x.lineTo(gx, ch); }
  for (let gx = ox - gcell; gx >= 0; gx -= gcell) { x.moveTo(gx, 0); x.lineTo(gx, ch); }
  for (let gy = oy; gy <= ch; gy += gcell) { x.moveTo(0, gy); x.lineTo(cw, gy); }
  for (let gy = oy - gcell; gy >= 0; gy -= gcell) { x.moveTo(0, gy); x.lineTo(cw, gy); }
  x.stroke();
  // room
  x.strokeStyle = INK; x.lineWidth = 2.5; x.strokeRect(ox, oy, rw, rh);
  x.fillStyle = 'rgba(255,255,255,0.03)'; x.fillRect(ox, oy, rw, rh);
  const bandRect = (wall, t) => wall === 'back' ? { x: ox, y: oy, w: rw, h: t } : wall === 'front' ? { x: ox, y: oy + rh - t, w: rw, h: t } : wall === 'left' ? { x: ox, y: oy, w: t, h: rh } : { x: ox + rw - t, y: oy, w: t, h: rh };
  // racks
  const rackPick = quote.racks && quote.racks[0];
  const holds = rackPick && input.bottles ? Math.min(input.bottles, rackPick.holds || input.bottles) : 0;
  const rackWalls = rackWallsFor(state.unitWall);
  let planRacks = null;
  if (holds) {
    const rdim = rackPick && rackPick.rack && rackPick.rack.dim;
    planRacks = planRackDist(holds, rackWalls, L, W, H, rdim);
    const depthFt = rdim ? (rdim.depth ? rdim.depth / 12 : (rdim.orient === 'label' ? 0.42 : 1.0)) : 0.9;
    const t = Math.max(6, Math.min(depthFt * scale, 30));
    x.save();
    planRacks.dist.forEach((d) => {
      if (!d.count) return; const r = bandRect(d.wall, t);
      x.fillStyle = 'rgba(127,224,255,0.14)'; x.fillRect(r.x, r.y, r.w, r.h);
      x.strokeStyle = CYAN; x.lineWidth = 1; x.strokeRect(r.x, r.y, r.w, r.h);
      // hatch
      x.beginPath(); const step = 8; const horiz = r.w > r.h;
      if (horiz) for (let hx = r.x; hx < r.x + r.w; hx += step) { x.moveTo(hx, r.y); x.lineTo(Math.min(hx + r.h, r.x + r.w), r.y + r.h); }
      else for (let hy = r.y; hy < r.y + r.h; hy += step) { x.moveTo(r.x, hy); x.lineTo(r.x + r.w, Math.min(hy + r.w, r.y + r.h)); }
      x.strokeStyle = 'rgba(127,224,255,0.28)'; x.stroke();
      // label
      x.fillStyle = CYAN; x.save(); const cx = r.x + r.w / 2, cy = r.y + r.h / 2;
      const txt = d.count.toLocaleString() + ' btl';
      if (!horiz) { x.translate(cx, cy); x.rotate(-Math.PI / 2); x.textAlign = 'center'; x.fillText(txt, 0, 0); } else { x.textAlign = 'center'; x.fillText(txt, cx, cy); }
      x.restore();
    });
    x.restore();
  }
  // cooling unit(s) on its wall
  const unitPick = quote.picks && quote.picks[0];
  if (unitPick) {
    const qty = Math.max(1, unitPick.qty || 1);
    const along = (state.unitWall === 'back' || state.unitWall === 'front') ? 'h' : 'v';
    // wall-line coordinate (wl) and inward sign toward the room (ins)
    let wl, ins;
    if (state.unitWall === 'back') { wl = oy; ins = 1; }
    else if (state.unitWall === 'left') { wl = ox; ins = 1; }
    else { wl = ox + rw; ins = -1; }
    const spanPx = along === 'h' ? rw : rh, spanStart = along === 'h' ? ox : oy;
    const fw = Math.min((unitPick.unit.dim && unitPick.unit.dim.w ? unitPick.unit.dim.w / 12 : 1.5) * scale, spanPx * 0.34);
    const dep = Math.max(7, Math.min(0.35 * scale, 13)); // through-wall unit is recessed: shallow cellar-side footprint, not floor-standing
    const gap = fw * 0.5, total = Math.min(qty * fw + (qty - 1) * gap, spanPx * 0.92);
    const step = qty > 1 ? (total - fw) / (qty - 1) : 0;
    const startC = (spanStart + spanPx / 2) - total / 2 + fw / 2;
    const p0 = wl - dep * (ins > 0 ? 0.55 : 0.45); // straddle the wall (mostly set into it), small room-side protrusion
    // recessed, wall-mounted unit — dashed outline = mounted above the plan cut plane (takes no floor space)
    x.save(); x.fillStyle = 'rgba(255,207,122,0.35)'; x.strokeStyle = AMBER; x.lineWidth = 1.5; x.setLineDash([4, 3]);
    for (let k = 0; k < qty; k++) {
      const c = startC + k * step;
      if (along === 'h') { x.fillRect(c - fw / 2, p0, fw, dep); x.strokeRect(c - fw / 2, p0, fw, dep); }
      else { x.fillRect(p0, c - fw / 2, dep, fw); x.strokeRect(p0, c - fw / 2, dep, fw); }
    }
    x.restore();
    const midU = startC + (qty - 1) * step / 2, halfU = fw / 2;
    x.fillStyle = AMBER; x.font = 'bold 10px system-ui'; x.textAlign = 'center';
    const lbl = (qty > 1 ? qty + '× cooling' : 'cooling') + ' (wall)';
    if (along === 'h') x.fillText(lbl, midU, state.unitWall === 'back' ? wl + dep + 9 : wl - dep - 7);
    else { x.save(); x.translate(state.unitWall === 'left' ? wl + dep + 9 : wl - dep - 9, midU); x.rotate(-Math.PI / 2); x.fillText(lbl, 0, 0); x.restore(); }
    // --- contractor callouts: symbols only (wording lives in INSTALL NOTES + key) ---
    let P, O, A;
    if (state.unitWall === 'back') { P = { x: midU, y: oy }; O = { x: 0, y: -1 }; A = { x: 1, y: 0 }; }
    else if (state.unitWall === 'left') { P = { x: ox, y: midU }; O = { x: -1, y: 0 }; A = { x: 0, y: 1 }; }
    else { P = { x: ox + rw, y: midU }; O = { x: 1, y: 0 }; A = { x: 0, y: 1 }; }
    // heat-exhaust arrow out through the wall
    x.strokeStyle = '#ff6b4a'; x.fillStyle = '#ff6b4a'; x.lineWidth = 2;
    const a1 = { x: P.x + O.x * 16, y: P.y + O.y * 16 }; // stays within the wall→dimension-line gap
    x.beginPath(); x.moveTo(P.x + O.x * 2, P.y + O.y * 2); x.lineTo(a1.x, a1.y); x.stroke();
    const ah = 5; x.beginPath();
    if (O.x === 0) { x.moveTo(a1.x, a1.y); x.lineTo(a1.x - ah, a1.y - O.y * ah); x.lineTo(a1.x + ah, a1.y - O.y * ah); }
    else { x.moveTo(a1.x, a1.y); x.lineTo(a1.x - O.x * ah, a1.y - ah); x.lineTo(a1.x - O.x * ah, a1.y + ah); }
    x.closePath(); x.fill();
    // duplex-outlet symbol inside the room, offset along the wall clear of the unit + its label
    const rx2 = P.x - O.x * 14 + A.x * (halfU + 13), ry2 = P.y - O.y * 14 + A.y * (halfU + 13);
    x.fillStyle = '#ffd27a'; x.strokeStyle = '#0f2f5e'; x.lineWidth = 1;
    x.beginPath(); x.arc(rx2, ry2, 5, 0, Math.PI * 2); x.fill(); x.stroke();
    x.fillStyle = '#0f2f5e'; x.fillRect(rx2 - 2.4, ry2 - 1.6, 1.3, 3.2); x.fillRect(rx2 + 1.1, ry2 - 1.6, 1.3, 3.2);
    x.font = '12px system-ui,-apple-system,sans-serif';
  }
  // door on front edge (+ swing)
  const dwFt = Math.min(3.0, L * 0.6), dw = dwFt * scale;
  const dcx = ox + rw / 2, dy = oy + rh;
  x.strokeStyle = '#0f2f5e'; x.lineWidth = 5; x.beginPath(); x.moveTo(dcx - dw / 2, dy); x.lineTo(dcx + dw / 2, dy); x.stroke(); // clear the wall for the opening
  x.strokeStyle = input.glass ? CYAN : INK; x.lineWidth = input.glass ? 3 : 2;
  x.beginPath(); x.moveTo(dcx - dw / 2, dy); x.lineTo(dcx + dw / 2, dy); x.stroke();
  x.strokeStyle = 'rgba(220,233,255,0.5)'; x.lineWidth = 1; x.beginPath(); x.arc(dcx - dw / 2, dy, dw, -Math.PI / 2, 0); x.stroke();
  x.beginPath(); x.moveTo(dcx - dw / 2, dy); x.lineTo(dcx - dw / 2, dy - dw); x.stroke();
  x.fillStyle = INK; x.textAlign = 'center'; x.fillText(input.glass ? 'glass door' : (input.standardDoor ? 'door' : 'entry'), dcx, dy + 16);
  // dimension lines
  x.strokeStyle = INK; x.fillStyle = INK; x.lineWidth = 1;
  const arrow = (x1, y1, x2, y2) => { x.beginPath(); x.moveTo(x1, y1); x.lineTo(x2, y2); x.stroke(); };
  arrow(ox, oy - 22, ox + rw, oy - 22); arrow(ox, oy - 26, ox, oy - 18); arrow(ox + rw, oy - 26, ox + rw, oy - 18);
  x.textAlign = 'center'; x.fillText(L + ' ft', ox + rw / 2, oy - 32);
  arrow(ox - 26, oy, ox - 26, oy + rh); arrow(ox - 30, oy, ox - 22, oy); arrow(ox - 30, oy + rh, ox - 22, oy + rh);
  x.save(); x.translate(ox - 40, oy + rh / 2); x.rotate(-Math.PI / 2); x.textAlign = 'center'; x.fillText(W + ' ft', 0, 0); x.restore();
  // north/back label
  x.fillStyle = 'rgba(220,233,255,0.6)'; x.textAlign = 'left'; x.fillText('back wall', ox + 4, oy - 7);
  // scale bar (bottom-left): pick a round footage that renders ~70px
  const targetPx = 70; let barFt = Math.max(1, Math.round(targetPx / scale));
  [1, 2, 3, 5, 10, 15, 20].some((n) => { if (n >= barFt) { barFt = n; return true; } return false; });
  const barPx = barFt * scale, sbx = mL, sby = ch - 22;
  x.strokeStyle = INK; x.fillStyle = INK; x.lineWidth = 2; x.textAlign = 'left';
  x.beginPath(); x.moveTo(sbx, sby); x.lineTo(sbx + barPx, sby); x.moveTo(sbx, sby - 4); x.lineTo(sbx, sby + 4); x.moveTo(sbx + barPx, sby - 4); x.lineTo(sbx + barPx, sby + 4); x.stroke();
  x.font = '11px system-ui,-apple-system,sans-serif'; x.fillText(barFt + ' ft  ·  grid = ' + gridFt + ' ft', sbx + barPx + 8, sby);
  // plan + install-notes block (desktop/tablet; phones use the legend + spec sheet)
  if (!narrow) {
    const facts = installFacts(input, quote);
    const bx = cw - mR + 8, by = oy, bw = mR - 12, bh = Math.min(ch - by - 12, 250); x.textAlign = 'left';
    x.fillStyle = 'rgba(9,24,51,0.95)'; x.fillRect(bx - 4, by - 4, bw, bh);
    x.strokeStyle = 'rgba(220,233,255,0.35)'; x.strokeRect(bx - 4, by - 4, bw, bh);
    x.fillStyle = INK; x.font = 'bold 12px system-ui'; x.fillText('CELLAR PLAN', bx, by + 10);
    x.font = '10px system-ui,-apple-system,sans-serif'; x.fillStyle = 'rgba(220,233,255,0.9)';
    const rows = [
      `${L} × ${W} × ${H} ft · ${(L * W * H).toLocaleString()} cu ft`,
      (input.bottles ? input.bottles.toLocaleString() + ' bottles' : 'no bottle count'),
      loadBtu(quote) ? `Load ~${loadBtu(quote).toLocaleString()} BTU/h (ΔT ${facts.deltaT}°F)` : '',
      facts.headroom != null ? `Capacity headroom +${facts.headroom}%` : '',
      '', '@INSTALL',
      '• Elec: 115V/15A dedicated',
      facts.tech ? '• Ducted split → remote' : `• Heat → adj. space`,
      facts.tech ? '   condenser · HVAC-R' : `   ≥ ${facts.exhaust.toLocaleString()} cu ft, <80°F`,
      '• Condensate ¼in/ft → drain',
      '• Mount ~6in below ceiling'
    ];
    let yy = by + 28;
    rows.forEach((ln) => {
      if (ln === '') { yy += 7; return; }
      if (ln === '@INSTALL') { x.fillStyle = INK; x.font = 'bold 10px system-ui'; x.fillText('INSTALL NOTES', bx, yy); x.font = '10px system-ui,-apple-system,sans-serif'; x.fillStyle = 'rgba(220,233,255,0.9)'; }
      else x.fillText(ln, bx, yy);
      yy += 14;
    });
    // symbol key
    yy += 6; x.font = '9px system-ui'; x.textAlign = 'left';
    x.fillStyle = '#ffd27a'; x.beginPath(); x.arc(bx + 4, yy - 3, 4, 0, 6.283); x.fill(); x.strokeStyle = '#0f2f5e'; x.lineWidth = 1; x.stroke();
    x.fillStyle = 'rgba(220,233,255,0.85)'; x.fillText('115V/15A outlet', bx + 13, yy - 3);
    yy += 14; x.strokeStyle = '#ff6b4a'; x.lineWidth = 2; x.beginPath(); x.moveTo(bx, yy - 3); x.lineTo(bx + 8, yy - 3); x.stroke();
    x.fillStyle = '#ff6b4a'; x.beginPath(); x.moveTo(bx + 8, yy - 3); x.lineTo(bx + 4, yy - 6); x.lineTo(bx + 4, yy); x.closePath(); x.fill();
    x.fillStyle = 'rgba(220,233,255,0.85)'; x.fillText('heat exhaust', bx + 13, yy - 3);
  }
  return { planRacks: planRacks, holds: holds };
}

/* ---- view management: blueprint (default) ⇄ lazy-loaded 3D ------------- */
let currentView = 'plan';
function injectToggle(mountEl) {
  let t = mountEl.querySelector('.wcc3d-toggle');
  if (t) return t;
  t = document.createElement('div'); t.className = 'wcc3d-toggle';
  t.style.cssText = 'position:absolute;top:12px;right:12px;z-index:6;display:flex;background:rgba(15,20,26,.72);border-radius:9px;padding:3px;backdrop-filter:blur(4px)';
  t.innerHTML = '<button type="button" data-v="plan" style="border:0;border-radius:7px;padding:6px 12px;font:600 13px system-ui;cursor:pointer;color:#fff;background:transparent">Floor plan</button>'
    + '<button type="button" data-v="3d" style="border:0;border-radius:7px;padding:6px 12px;font:600 13px system-ui;cursor:pointer;color:#fff;background:transparent">3D view</button>';
  t.addEventListener('click', (e) => { const b = e.target.closest('[data-v]'); if (b) showView(b.getAttribute('data-v')); });
  mountEl.appendChild(t);
  return t;
}
function updateToggle(mountEl) {
  const t = mountEl && mountEl.querySelector('.wcc3d-toggle'); if (!t) return;
  [].forEach.call(t.querySelectorAll('[data-v]'), (b) => { const on = b.getAttribute('data-v') === currentView; b.style.background = on ? '#7a5cff' : 'transparent'; b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
}
function loadingOverlay(mountEl, on) {
  let o = mountEl.querySelector('.wcc3d-loading');
  if (on) {
    if (!o) { o = document.createElement('div'); o.className = 'wcc3d-loading'; o.style.cssText = 'position:absolute;inset:0;z-index:4;display:flex;align-items:center;justify-content:center;background:#15181c;color:#cfe0ff;font:14px system-ui'; o.textContent = 'Loading 3D…'; mountEl.appendChild(o); }
  } else if (o) o.remove();
}
function showView(v, opts) {
  opts = opts || {};
  if (!state.lastInput || !state.lastMount) return;
  const mount = state.lastMount, bar = document.getElementById('WccQuote3DBar');
  currentView = v;
  if (v === '3d') {
    if (bar) bar.style.display = '';
    const tog = mount.querySelector('.wcc3d-toggle');
    loadingOverlay(mount, true);
    loadThree().then(() => {
      const meta = render(mount, state.lastInput, state.lastQuote);
      loadingOverlay(mount, false);
      injectToggle(mount); updateToggle(mount);
      if (!meta) { showView('plan'); return; }
      wireBar(); wireWalls(); highlightPreset('overview');
      updateCaption(meta, state.lastQuote, state.lastCat);
    }).catch((e) => { warn('3D load failed', e); loadingOverlay(mount, false); currentView = 'plan'; showView('plan'); });
  } else {
    if (S) fullTeardown();
    if (bar) bar.style.display = 'none';
    renderBlueprint(mount, state.lastInput, state.lastQuote, state.lastCat);
    injectToggle(mount); updateToggle(mount);
    updateCaption({ rackInfo: state.lastPlanInfo }, state.lastQuote, state.lastCat);
  }
}

/* ---- contractor install facts (sourced: WhisperKOOL/Wine Guardian/US Cellar) - */
function installFacts(input, quote) {
  const p = quote.picks && quote.picks[0];
  const vol = (input.length || 0) * (input.width || 0) * (input.height || 0);
  const tech = !!(p && p.unit && p.unit.tech);
  const exhaust = Math.round(vol * 2 / 10) * 10;
  const headroom = (p && quote.sizingVolume) ? Math.round((p.covered / quote.sizingVolume - 1) * 100) : null;
  return {
    tech: tech,
    electrical: tech
      ? 'Dedicated circuit for the condenser (breaker per model) + 115V/15A for the evaporator. No shared or switched loads.'
      : 'Dedicated 115V/15A circuit with a receptacle within reach of the unit. No shared loads, switched outlets, or extension cords. (Confirm amperage per model.)',
    heat: tech
      ? 'Ducted split: insulated copper refrigerant line-set brazed on-site to a remote condenser sited in a ventilated space. Licensed HVAC-R install required.'
      : 'Self-contained: rejects heat out the back into an adjacent space — size it ≥ ' + exhaust.toLocaleString() + ' cu ft (≈2× the cellar), keep it under 80°F, and ventilate it (garage, utility, or mechanical room).',
    condensate: 'Route a condensate drain line at ¼ in per ft of slope to a floor drain, sump, or condensate pump.',
    mount: 'Mount high on the wall, ~6 in below the ceiling, centered on the wall for even air distribution.',
    envelope: 'Envelope per calculator inputs: R-value ' + (input.rFactor || 0.055) + ', ' + (input.vapourBarrier ? 'vapor barrier installed' : 'NO vapor barrier (+15% load)') + (input.glass ? ', glass wall/door (+glass load)' : '') + (input.standardDoor ? ', standard door (+10%)' : '') + '.',
    ambient: input.ambient, deltaT: Math.max((input.ambient || 75) - 55, 10),
    load: loadBtu(quote), sizingVol: quote.sizingVolume, covered: p ? p.covered : null, headroom: headroom, exhaust: exhaust
  };
}

/* ---- always-visible legend (the reliable alternative to hover) --------- */
function buildLegend(wrap, mountEl, quote, cat, facts) {
  if (!wrap) return;
  let lg = wrap.querySelector('.calc-3d-legend');
  if (!lg) {
    lg = document.createElement('div'); lg.className = 'calc-3d-legend';
    lg.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;justify-content:center;margin:14px auto 0;max-width:900px';
    if (mountEl && mountEl.parentNode) mountEl.parentNode.insertBefore(lg, mountEl.nextSibling); else wrap.appendChild(lg);
  }
  const money = (n) => '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const card = (img, kicker, title, lines, url) =>
    '<div style="flex:1 1 320px;max-width:430px;display:flex;gap:11px;align-items:flex-start;background:#f7f5f0;border:1px solid #e6e0d4;border-radius:12px;padding:11px 13px;text-align:left">'
    + (img ? '<img src="' + esc(thumb(img, 220)) + '" alt="" style="width:66px;height:66px;object-fit:contain;background:#fff;border-radius:7px;flex:none">' : '')
    + '<div style="min-width:0"><div style="font-size:.72rem;letter-spacing:.04em;text-transform:uppercase;color:#8a7c58;font-weight:700">' + esc(kicker) + '</div>'
    + '<div style="font-weight:700;font-size:.95rem;line-height:1.25;margin:1px 0 3px">' + esc(title) + '</div>'
    + '<div style="font-size:.82rem;color:#555;line-height:1.45">' + lines + '</div>'
    + (url ? '<a href="' + esc(url) + '" target="_blank" rel="noopener" style="display:inline-block;margin-top:5px;font-size:.82rem;font-weight:600;color:#7a5cff;text-decoration:none">View product &rarr;</a>' : '')
    + '</div></div>';
  let html = '';
  const p = quote.picks && quote.picks[0];
  if (p) {
    const u = p.unit, qty = Math.max(1, p.qty || 1);
    const lines = (qty > 1 ? qty + ' units · ' : '') + 'rated ' + Number(u.cuft).toLocaleString() + ' cu ft each'
      + (p.variant ? ' · ' + money(p.variant.price) + (qty > 1 ? ' ea' : '') : '')
      + '<br>' + (facts.tech ? '<b style="color:#b4682a">Ducted split — HVAC-R install</b>' : 'Self-contained · through-wall')
      + ' · ' + (facts.load ? '~' + Number(facts.load).toLocaleString() + ' BTU/h load' : '');
    html += card(u.img, 'Cooling unit', u.title, lines, u.url);
  }
  const r = quote.racks && quote.racks[0];
  if (r) {
    const orient = r.rack.dim && r.rack.dim.orient;
    const lines = (r.holds ? r.holds.toLocaleString() + ' bottles · ' : '') + r.qty + ' rack' + (r.qty > 1 ? 's' : '') + ' · ' + money(r.total)
      + (orient ? '<br>' + (orient === 'cork' ? 'Cork-out (bottles face out)' : orient === 'label' ? 'Label-out (labels face the room)' : 'Display rack') : '');
    html += card(r.rack.img, 'Racking', r.rack.title, lines, r.rack.url);
  }
  lg.innerHTML = html;
  lg.style.display = html ? 'flex' : 'none';
}

function renderForm(formEl, mountEl) {
  const wrap = mountEl && (mountEl.closest('.calc-3d-section') || mountEl.closest('.calc-3d') || mountEl.parentNode);
  try {
    if (!formEl || !mountEl || !window.WCCQuote) return null;
    const cat = catalog(); if (!cat) return null;
    const input = readInput(formEl); if (!input.length || !input.width || !input.height) return null;
    const q = window.WCCQuote.quote(input, cat);
    state.lastInput = input; state.lastQuote = q; state.lastMount = mountEl; state.lastForm = formEl; state.lastCat = cat;
    if (wrap && wrap.style) wrap.style.display = '';
    wireWalls();
    buildShareBar(wrap, formEl, q, cat);
    buildLegend(wrap, mountEl, q, cat, installFacts(input, q));
    syncUrl(formEl, state.unitWall);
    if (currentView === '3d') { showView('3d'); }
    else {
      const p = renderBlueprint(mountEl, input, q, cat);
      state.lastPlanInfo = p && p.planRacks ? { shown: p.planRacks.shown, holds: p.holds, truncated: p.planRacks.truncated, wallsUsed: p.planRacks.dist.filter((d) => d.count > 0).length } : null;
      injectToggle(mountEl); updateToggle(mountEl);
      const bar = document.getElementById('WccQuote3DBar'); if (bar) bar.style.display = 'none';
      updateCaption({ rackInfo: state.lastPlanInfo }, q, cat);
    }
    return true;
  } catch (e) { warn('renderForm failed', e); fallbackCard(wrap, mountEl); return null; }
}
window.WCCQuote3D = { render, renderForm, setPreset, setUnitWall, showView, openSpecSheet: () => state.lastForm && openSpecSheet(state.lastForm, state.lastQuote, state.lastCat) };

function init() {
  const form = document.getElementById('WccQuoteForm'); const mountEl = document.getElementById('WccQuote3D');
  if (!form || !mountEl) return;
  const wrap = mountEl.closest('.calc-3d-section') || mountEl.closest('.calc-3d') || mountEl.parentNode; const cat = catalog();
  if (!window.WCCQuote || !cat) { if (wrap) wrap.style.display = 'none'; warn('engine or catalog missing'); return; }
  // shared link → prefill the form (and unit wall) before first render
  try { const p = new URLSearchParams(location.search); const uw = p.get('uw'); if (uw && ['left', 'back', 'right'].indexOf(uw) >= 0) state.unitWall = uw; } catch (e) {}
  applyUrlToForm(form);
  const run = () => renderForm(form, mountEl);
  form.addEventListener('submit', () => setTimeout(run, 0)); window.WCCQuote3D.run = run; run();
}
try { ready(init); } catch (e) { warn('init threw', e); /* leave the 2D calculator untouched */ }
